// content/zones/zone6.ts — THE GLOOMHEART's zone mechanic.
//
//   DESIGN §5 Zone 6: "the deep remembers — a hub of three trials remixing the
//   verbs you learned, on one unbroken clock."
//
// This file is three programs the player has already been taught, running one
// per spur, plus the one new thing the zone owns: a meter that opens at 40% and
// never resets, rendered into the room itself so the zone IS the clock.
//
//   THE HUB          the Heartchamber. Altar, fountain, heart-gate, three
//                    gullets. The Gloom holds here by respite semantics — the
//                    only breath in the zone, and you choose what to spend it on.
//   THE DROWNING SPUR  zone 2's floor-rhythm, on an 18-second beat instead of 25,
//                    with the causeway broken and the stone sparse.
//   THE PALE SPUR    zone 3's husk rule, at a 2-second timer, Gravetenders in
//                    pairs.
//   THE DARK SPUR    zone 4's sight economy, at two braziers, with the Unlit
//                    walking from the moment the gullet opens.
//   THE ARENA        all three at once, driven by content/bosses/boss6.ts through
//                    the bridge exported below. The boss owns what a memory
//                    MEANS; this file owns how it is drawn and what it does to
//                    the floor.
//
// Every literal comes from data/zones/zone6.ts. The numbers in this file are
// drawing geometry and nothing else.
//
// ---------------------------------------------------------------------------
// FLAGGED — why nothing here imports another zone's CODE.
//
// The bodies are re-dressed imports (data/zones/zone6.ts), but every brain is
// written fresh. Running zone 2's Tidecaller brain in this zone would silently
// break it: that brain gates its hook on zone 2's module-level water cache,
// which is emptied the moment the Drowned Roots end. Same story for the
// Glintmoth (reads zone 4's light registry) and the Mireskipper's wake (reads
// zone 2's tile array). Data is safe to import; module state is not.
// ---------------------------------------------------------------------------

import type { Draw, EliteRider, Enemy, Room, RunCtx, Vec, ZoneMechanic } from '../../types'
import {
  ZONE6,
  ZONE6_BOSS_ARENA,
  ZONE6_COLORS,
  ZONE6_DARK,
  ZONE6_DROWN,
  ZONE6_ENEMIES,
  ZONE6_GENERATORS,
  ZONE6_GLOOM,
  ZONE6_PALE,
  ZONE6_PROJECTILES,
  ZONE6_REFORMED,
} from '../../data/zones/zone6'
import { registerZone, registerZoneContent } from '../../game/zones'
import { moveEnemy, pursue, registerEnemyBrain, roleBrain } from '../../game/enemies'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import { emit } from '../../engine/particles'
import { fx } from '../../engine/fx'
import { LOGICAL_H, LOGICAL_W } from '../../engine/render'
import { TILE } from '../../game/world'
import type { EnemyEnt } from '../../game/entities'
import type { Run } from '../../game/game'

const C = ZONE6_COLORS
const DROWN = ZONE6_DROWN
const SURGE = ZONE6_DROWN.surge
const PALE = ZONE6_PALE
const DARK = ZONE6_DARK
const BR = ZONE6_DARK.brazier
const U = ZONE6_DARK.unlit
const TAU = Math.PI * 2
const DEG = Math.PI / 180

// ===========================================================================
// 0. THE SURFACES RunCtx DOES NOT CARRY
// types.ts is frozen; three things this zone needs live on the Run object it is
// handed but not on the interface. Read structurally — the pattern zone 2
// established for relicFlags (D-020) and zone 4 extended.
// ===========================================================================

interface Projectilish {
  alive: boolean
  friendly: boolean
  pos: Vec
  radius: number
  color: string
  spriteId: string
  rot: number
  def: { kind: string }
}

interface RunInternals {
  relicFlags?: Record<string, number>
  flags?: Record<string, number | boolean>
  projectilePool?: { live: Projectilish[] }
}

const NO_FLAGS: Record<string, number> = {}
function relicFlags(ctx: RunCtx): Record<string, number> {
  return (ctx as unknown as RunInternals).relicFlags ?? NO_FLAGS
}
function metaFlag(ctx: RunCtx, key: string): number {
  const v = (ctx as unknown as RunInternals).flags?.[key]
  return typeof v === 'number' ? v : 1
}

/** The runtime fields on a body that the frozen Enemy shape does not list. */
type Body = Enemy & { radius: number; telegraph: number; bob: number; state: number; facing: number }

function num(v: unknown, d: number): number {
  return typeof v === 'number' ? v : d
}
function ai(e: { def: { ai: Record<string, unknown> } }, k: string, d: number): number {
  return num(e.def.ai[k], d)
}

// ===========================================================================
// 1. THE ROOM — geometry cached once per room, never per frame
// ===========================================================================

const T_OTHER = 0
const T_WATER = 1
const T_STONE = 2

let gw = 0
let gh = 0
let tiles = new Uint8Array(0)
/** World centres of every 'W' tile — the Tidecallers' target set. */
let waterTiles: Vec[] = []
let floorLayer: HTMLCanvasElement | null = null
const current: Vec = { x: 0, y: 1 }

function tileKind(tx: number, ty: number): number {
  if (tx < 0 || ty < 0 || tx >= gw || ty >= gh) return T_OTHER
  return tiles[ty * gw + tx]
}

export function z6IsWaterAt(x: number, y: number): boolean {
  return tileKind(Math.floor(x / TILE), Math.floor(y / TILE)) === T_WATER
}

// --- ripples: a fixed pool the brains and the flood both draw into ----------

interface Ripple {
  x: number
  y: number
  r0: number
  r1: number
  t: number
  life: number
  color: string
}
const ripples: Ripple[] = []
for (let i = 0; i < 24; i++) ripples.push({ x: 0, y: 0, r0: 0, r1: 0, t: 0, life: 1, color: C.caustic })
let rippleHead = 0

function pushRipple(x: number, y: number, r0: number, r1: number, life: number, color: string): void {
  const r = ripples[rippleHead]
  rippleHead = (rippleHead + 1) % ripples.length
  r.x = x
  r.y = y
  r.r0 = r0
  r.r1 = r1
  r.t = life
  r.life = life
  r.color = color
}

function splash(x: number, y: number, count: number): void {
  if (z6IsWaterAt(x, y)) {
    emit(x, y, {
      count,
      color: C.caustic,
      speed: 130,
      speedVar: 90,
      life: 0.34,
      lifeVar: 0.16,
      size: 3,
      grav: 260,
      shape: 1,
    })
    pushRipple(x, y, 4, 26, 0.45, C.caustic)
  } else {
    emit(x, y, { count: Math.max(3, count >> 1), color: C.fleshHi, speed: 90, life: 0.24, size: 2 })
  }
}

// ===========================================================================
// 2. THE LIGHT — one registry, shared by the mechanic, the brains and the boss
// ===========================================================================

export interface Z6Brazier {
  x: number
  y: number
  tx: number
  ty: number
  lit: boolean
  fuel: number
  flare: number
  smoke: number
}

function brazierRadius(b: Z6Brazier): number {
  if (!b.lit) return 0
  const k = b.fuel < BR.gutterSeconds ? 0.55 + 0.45 * Math.max(0, b.fuel / BR.gutterSeconds) : 1
  return BR.radius * k
}

function falloff(d: number, r: number, core: number): number {
  if (r <= 0 || d >= r) return 0
  const inner = r * core
  if (d <= inner) return 1
  return 1 - (d - inner) / (r - inner)
}

/** Exported because content/bosses/boss6.ts is the other half of this zone. */
export const zone6Lights = {
  braziers: [] as Z6Brazier[],
  lampRadius: DARK.lampRadius as number,
  lampX: 0,
  lampY: 0,
  /** true only where the dark is actually on (the dark spur, and P3's arena). */
  dark: false,

  litCount(): number {
    let n = 0
    for (let i = 0; i < this.braziers.length; i++) if (this.braziers[i].lit) n++
    return n
  },

  nearestUnlit(x: number, y: number, reach: number): number {
    let best = -1
    let bd = reach * reach
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (b.lit) continue
      const d = (b.x - x) * (b.x - x) + (b.y - y) * (b.y - y)
      if (d <= bd) {
        bd = d
        best = i
      }
    }
    return best
  },

  inBrazierLight(x: number, y: number): boolean {
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const r = brazierRadius(b)
      const dx = b.x - x
      const dy = b.y - y
      if (dx * dx + dy * dy <= r * r) return true
    }
    return false
  },

  /** 0 = pitch dark, 1 = fully lit. Brightness, for rendering and for brains. */
  lightAt(x: number, y: number): number {
    if (!this.dark) return 1
    let best = falloff(Math.hypot(this.lampX - x, this.lampY - y), this.lampRadius, DARK.lampCore)
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const v = falloff(Math.hypot(b.x - x, b.y - y), brazierRadius(b), BR.core)
      if (v > best) best = v
    }
    return best
  },

  nearestSource(x: number, y: number, out: Vec): void {
    let bd = Math.hypot(this.lampX - x, this.lampY - y) - this.lampRadius
    out.x = this.lampX
    out.y = this.lampY
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const d = Math.hypot(b.x - x, b.y - y) - brazierRadius(b)
      if (d < bd) {
        bd = d
        out.x = b.x
        out.y = b.y
      }
    }
  },
}

/** Light one. Exported: the Sovereign's memory of the dark opens them lit. */
export function z6LightBrazier(i: number, seconds: number): void {
  const b = zone6Lights.braziers[i]
  if (!b || b.lit) return
  b.lit = true
  b.fuel = seconds
  b.flare = 0.45
  b.smoke = 0
  emit(b.x, b.y - 10, {
    count: 26,
    color: C.lampHot,
    speed: 190,
    speedVar: 130,
    life: 0.6,
    lifeVar: 0.25,
    size: 4,
    grav: -90,
    shape: 1,
  })
}

export function z6DouseBrazier(i: number): void {
  const b = zone6Lights.braziers[i]
  if (!b || !b.lit) return
  b.lit = false
  b.fuel = 0
  b.smoke = 1.4
  emit(b.x, b.y - 8, { count: 14, color: '#2a2135', speed: 90, life: 0.9, size: 4, grav: -40, shape: 1 })
}

// ===========================================================================
// 3. THE ARENA BRIDGE
// The Sovereign wields this zone's own rules, so it writes here and this file
// draws. Both files are zone 6's; nothing shared learns anything.
// ===========================================================================

export interface Z6Band {
  /** 0 dry · 0<k<=1 telegraph progress · -1 flooded */
  state: number
  x: number
  y: number
  w: number
  h: number
}

export interface Z6Lane {
  on: boolean
  x0: number
  y0: number
  x1: number
  y1: number
  /** 0..1 telegraph progress; <0 means the charge itself is running. */
  k: number
}

export const zone6Arena = {
  active: false,
  /** P1's flooded edges, in world px. */
  bands: [] as Z6Band[],
  /** P2: escorts leave husks. */
  husks: false,
  /** P3: the chamber goes dark. */
  dark: false,
  /** The Sovereign's own light — it is never invisible (DESIGN's fairness line). */
  sovereign: { on: false, x: 0, y: 0, r: 150 },
  /** The husk-heart, while its five seconds run: also never hidden. */
  heart: { on: false, x: 0, y: 0, t: 0 },
  /** Glint-lanes for the chained charges (boss 4's telegraph language). */
  lanes: [] as Z6Lane[],
  /** Screen-wide pulse on the kill. */
  recoil: 0,
}
for (let i = 0; i < 3; i++) zone6Arena.lanes.push({ on: false, x0: 0, y0: 0, x1: 0, y1: 0, k: 0 })

/**
 * The flood's damage, in one place, used by BOTH the spur surge and the
 * Sovereign's edge floods. 15 dmg/s on a tick spaced just above the Warden's
 * post-hit grace, plus a current that shoves.
 */
export interface Z6FloodAcc {
  dmg: number
  push: number
}

export function z6FloodHurt(
  ctx: RunCtx,
  dt: number,
  inside: boolean,
  acc: Z6FloodAcc,
  pushX: number,
  pushY: number,
  cfg: { damagePerSecond: number; damageTickSeconds: number; push: number; pushTickSeconds: number },
): void {
  acc.dmg += dt
  acc.push += dt
  if (!inside) {
    // hold the accumulators at the brink so stepping in costs immediately
    if (acc.dmg > cfg.damageTickSeconds) acc.dmg = cfg.damageTickSeconds
    if (acc.push > cfg.pushTickSeconds) acc.push = cfg.pushTickSeconds
    return
  }
  if (acc.dmg >= cfg.damageTickSeconds) {
    acc.dmg = 0
    ctx.damagePlayer(cfg.damagePerSecond * cfg.damageTickSeconds, 'flood')
  }
  if (acc.push >= cfg.pushTickSeconds) {
    acc.push = 0
    ctx.pushPlayer(pushX * cfg.push, pushY * cfg.push)
    pushRipple(ctx.player.pos.x, ctx.player.pos.y, 8, 46, 0.4, C.floodHot)
  }
}

/**
 * Lay a husk where a body fell. Exported so the Sovereign's Memory of the Court
 * uses the same program the Pale Spur does — one husk rule in this zone, not two.
 */
export function z6LayHusk(ctx: RunCtx, x: number, y: number, srcId: string, elite: EliteRider | null, seconds: number): void {
  posScratch.x = x
  posScratch.y = y
  const h = ctx.spawnEnemy(PALE.huskId, posScratch, { elite: null, silent: true }) as EnemyEnt
  if (!h.alive) return
  // The frozen tag contract: gloomspawn = never counted by the room-clear
  // condition, never pays a kill. A husk is a job on the floor, not a body.
  h.tags.gloomspawn = true
  h.tags.noKnockback = true
  h.tags.stunProof = true // a stun must never pause the seal/reform brain (D-031)
  h.tags.huskT = seconds
  h.tags.huskMax = seconds
  h.tags.srcId = srcId
  h.tags.srcElite = elite ?? ''
  emit(x, y, { count: 12, color: C.huskGlow, speed: 60, speedVar: 40, life: 0.7, lifeVar: 0.25, size: 3, shape: 1 })
  ctx.sfx('ui')
}

// ===========================================================================
// 4. THE BRAINS
// One per borrowed silhouette. Recognisable, and violet-wrong.
// ===========================================================================

const brainDir: Vec = { x: 0, y: 0 }
const sepDir: Vec = { x: 0, y: 0 }
const posScratch: Vec = { x: 0, y: 0 }
const srcScratch: Vec = { x: 0, y: 0 }
const hookScratch: Vec = { x: 0, y: 0 }

function angleDelta(a: number, b: number): number {
  let x = b - a
  while (x > Math.PI) x -= TAU
  while (x < -Math.PI) x += TAU
  return x
}

/** Local separation — game/enemies.ts keeps its own copy private. */
function localSep(run: Run, e: EnemyEnt, out: Vec): void {
  out.x = 0
  out.y = 0
  const list = run.enemyPool.live
  const reach = e.radius * 2.4
  run.hash.query(e.pos.x, e.pos.y, reach, (i) => {
    const o = list[i]
    if (o === e || !o.alive) return
    const dx = e.pos.x - o.pos.x
    const dy = e.pos.y - o.pos.y
    const d2 = dx * dx + dy * dy
    const want = e.radius + o.radius
    if (d2 >= want * want) return
    if (d2 < 0.0001) {
      out.x += 0.7
      out.y -= 0.3
      return
    }
    const d = Math.sqrt(d2)
    const push = (want - d) / want
    out.x += (dx / d) * push
    out.y += (dy / d) * push
  })
}

// --- Drowned Skipper: the swarm archetype, wearing a wake --------------------

const swarmBrain = roleBrain('swarm')

registerEnemyBrain('z6-skipper', (run, e, dt, speedMul) => {
  const wasAir = e.tags.z6air === true
  swarmBrain(run, e, dt, speedMul)
  const isAir = e.state === 2 // the shared brain's committed lunge
  if (isAir !== wasAir) {
    e.tags.z6air = isAir
    splash(e.pos.x, e.pos.y, isAir ? 7 : 11)
    if (!isAir) run.sfx('hit')
  }
})

// --- Gloomed Shellback: the shell you have to get around ---------------------
// Zone 2's reading, re-implemented: the body itself decides whether it is
// covered, and holds tags.invulnerable only while the Warden stands inside its
// frontal arc. Flanking drops the tag on the same frame; so does the whole
// windup/snap/recover window, so "wait for the lunge-miss" pays as well.

const S_APPROACH = 0
const S_WINDUP = 1
const S_SNAP = 2
const S_RECOVER = 3

registerEnemyBrain('z6-shellback', (run, e, dt, speedMul) => {
  const halfArc = (ai(e, 'frontalArcDeg', 130) * DEG) / 2
  const turnRate = ai(e, 'turnRate', 2)
  const lungeRange = ai(e, 'lungeRange', 112)
  const lungeCd = ai(e, 'lungeCooldown', 3)
  const windup = ai(e, 'windup', 0.55)
  const lungeSpeed = ai(e, 'lungeSpeed', 470)
  const lungeTime = ai(e, 'lungeTime', 0.3)
  const snapDamage = ai(e, 'snapDamage', 1.6)
  const recover = ai(e, 'recover', 1.2)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const toPlayer = Math.atan2(dy, dx)

  let face = num(e.tags.z6face, toPlayer)
  if (e.state === S_APPROACH || e.state === S_RECOVER) {
    const d = angleDelta(face, toPlayer)
    const step = turnRate * dt * (e.state === S_RECOVER ? 0.35 : 1)
    face += Math.max(-step, Math.min(step, d))
  }
  e.tags.z6face = face
  if (Math.abs(Math.cos(face)) > 0.2) e.facing = Math.cos(face) < 0 ? -1 : 1

  const covered = Math.abs(angleDelta(face, toPlayer)) <= halfArc
  const blocking = e.state === S_APPROACH && covered && e.spawnGrace <= 0
  e.tags.invulnerable = blocking
  e.tags.z6block = blocking

  if (e.state === S_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, -Math.cos(face) * 26, -Math.sin(face) * 26, dt)
    if (e.windup <= 0) {
      e.state = S_SNAP
      e.stateT = 0
      e.tags.z6hit = false
      e.kbx = Math.cos(face) * lungeSpeed
      e.kby = Math.sin(face) * lungeSpeed
      run.sfx('swing')
      splash(e.pos.x, e.pos.y, 8)
    }
    return
  }

  if (e.state === S_SNAP) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.tags.z6hit !== true && dist < e.radius + run.player.radius + 8) {
      e.tags.z6hit = true
      run.damagePlayer(e.touchDamage * snapDamage, e.def.id)
    }
    if (e.stateT > lungeTime) {
      e.state = S_RECOVER
      e.stateT = 0
      splash(e.pos.x, e.pos.y, 10)
    }
    return
  }

  if (e.state === S_RECOVER) {
    moveEnemy(run, e, -Math.cos(face) * 18, -Math.sin(face) * 18, dt)
    if (e.stateT > recover) {
      e.state = S_APPROACH
      e.stateT = 0
      e.cool = lungeCd
    }
    return
  }

  pursue(run, e, brainDir)
  const sp = e.speed * speedMul * (covered ? 1 : 0.82)
  moveEnemy(run, e, brainDir.x * sp, brainDir.y * sp, dt)
  if (dist < lungeRange + run.player.radius && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = S_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- Gloomed Tidecaller: the hook that wants you in the low ground -----------
// Dodge-cancelable, exactly as zone 2 taught it: i-frames when a pulse lands
// shear the hook and cancel the rest.

const B_IDLE = 0
const B_CHANNEL = 1
const B_PULL = 2
const B_REST = 3

function hookTarget(px: number, py: number, ahead: number, out: Vec): boolean {
  if (waterTiles.length === 0) return false
  const axx = px + current.x * ahead
  const ayy = py + current.y * ahead
  let best = -1
  let bestD = Infinity
  for (let i = 0; i < waterTiles.length; i++) {
    const t = waterTiles[i]
    const dx = t.x - axx
    const dy = t.y - ayy
    const d = dx * dx + dy * dy
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  const t = waterTiles[best]
  const dx = t.x - px
  const dy = t.y - py
  const l = Math.hypot(dx, dy)
  if (l < 36) {
    out.x = current.x
    out.y = current.y
    return true
  }
  out.x = dx / l
  out.y = dy / l
  return true
}

registerEnemyBrain('z6-tidecaller', (run, e, dt, speedMul) => {
  const keep = ai(e, 'keepRange', 250)
  const minRange = ai(e, 'minRange', 200)
  const hookRange = ai(e, 'hookRange', 430)
  const hookCd = ai(e, 'hookCooldown', 4.4)
  const channel = ai(e, 'channel', 1.5)
  const pulses = ai(e, 'pulses', 3)
  const pulseGap = ai(e, 'pulseGap', 0.3)
  const impulse = ai(e, 'pullImpulse', 360)
  const ahead = ai(e, 'dragAhead', 128)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const los = run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1

  if (e.state === B_CHANNEL) {
    e.windup -= dt
    e.telegraph = Math.max(e.telegraph, 0.001)
    moveEnemy(run, e, 0, 0, dt)
    e.tags.z6beam = 1
    if (!los || dist > hookRange * 1.25) {
      e.state = B_REST
      e.stateT = 0
      e.tags.z6beam = 0
      e.cool = hookCd * 0.5
      return
    }
    if (e.windup <= 0) {
      e.state = B_PULL
      e.stateT = 0
      e.windup = 0
      e.tags.z6pulses = pulses
      e.tags.z6beam = 2
      run.sfx('potion')
    }
    return
  }

  if (e.state === B_PULL) {
    moveEnemy(run, e, 0, 0, dt)
    e.tags.z6beam = 2
    e.windup -= dt
    if (e.windup <= 0) {
      const left = num(e.tags.z6pulses, 0)
      if (run.player.iframes > 0) {
        // the dodge: any i-frame source shears the hook
        e.tags.z6pulses = 0
        e.tags.z6beam = 0
        e.state = B_REST
        e.stateT = 0
        e.cool = hookCd
        run.sfx('ui')
        emit(px, py, { count: 12, color: C.hook, speed: 200, life: 0.3, size: 3, shape: 1 })
        return
      }
      if (hookTarget(px, py, ahead, hookScratch)) {
        run.pushPlayer(hookScratch.x * impulse, hookScratch.y * impulse)
        pushRipple(px, py, 6, 40, 0.4, C.hook)
        run.sfx('dash')
      }
      e.tags.z6pulses = left - 1
      e.windup = pulseGap
      if (left - 1 <= 0) {
        e.state = B_REST
        e.stateT = 0
        e.tags.z6beam = 0
        e.cool = hookCd
      }
    }
    return
  }

  if (e.state === B_REST) {
    e.tags.z6beam = 0
    pursue(run, e, brainDir)
    const sp = e.speed * 0.7 * speedMul
    moveEnemy(run, e, -brainDir.x * sp, -brainDir.y * sp, dt)
    if (e.stateT > 0.6) {
      e.state = B_IDLE
      e.stateT = 0
    }
    return
  }

  e.tags.z6beam = 0
  const sp = e.speed * speedMul
  let mx = 0
  let my = 0
  if (dist < minRange) {
    mx = -(dx / dist)
    my = -(dy / dist)
  } else if (dist > keep || !los) {
    pursue(run, e, brainDir)
    mx = brainDir.x
    my = brainDir.y
  } else {
    const s = e.bob * 0.6 + e.pos.x * 0.01
    mx = (-dy / dist) * Math.sin(s)
    my = (dx / dist) * Math.sin(s)
  }
  moveEnemy(run, e, mx * sp, my * sp, dt)

  if (e.cool <= 0 && e.spawnGrace <= 0 && dist < hookRange && los && waterTiles.length > 0) {
    e.state = B_CHANNEL
    e.stateT = 0
    e.windup = channel
    e.telegraph = channel
    e.tags.z6beam = 1
    run.sfx('telegraph')
  }
})

// --- Digested Courtier: the two-step lunge behind a red glint ----------------

const K_APPROACH = 0
const K_WINDUP = 1
const K_STEP = 2
const K_RECOVER = 3

function courtierLock(run: Run, e: EnemyEnt): void {
  const dx = run.player.pos.x - e.pos.x
  const dy = run.player.pos.y - e.pos.y
  const l = Math.hypot(dx, dy) || 1
  e.tags.lx = dx / l
  e.tags.ly = dy / l
  e.stateT = 0
  e.facing = dx < 0 ? -1 : 1
}

function courtierStrike(run: Run, e: EnemyEnt): void {
  const arc = ai(e, 'strikeArc', 1.3)
  const range = ai(e, 'strikeRange', 52)
  const mul = ai(e, 'strikeDamage', 1)
  const lx = num(e.tags.lx, 1)
  const ly = num(e.tags.ly, 0)
  const dir = Math.atan2(ly, lx)
  const pdx = run.player.pos.x - e.pos.x
  const pdy = run.player.pos.y - e.pos.y
  const pd = Math.hypot(pdx, pdy)
  let inArc = pd <= range + run.player.radius + e.radius
  if (inArc && pd > 2) {
    let a = Math.atan2(pdy, pdx) - dir
    while (a > Math.PI) a -= TAU
    while (a < -Math.PI) a += TAU
    inArc = Math.abs(a) <= arc / 2
  }
  if (inArc) run.damagePlayer(e.touchDamage * mul, e.def.id)
  run.sfx('swing')
  emit(e.pos.x + lx * 22, e.pos.y + ly * 22, {
    count: 5,
    color: '#ff8a6a',
    speed: 140,
    life: 0.16,
    size: 2,
    angle: dir,
    spread: arc,
    shape: 1,
  })
}

registerEnemyBrain('z6-courtier', (run, e, dt, speedMul) => {
  const lungeRange = ai(e, 'lungeRange', 156)
  const cd = ai(e, 'lungeCooldown', 2.5)
  const windup = ai(e, 'windup', 0.46)
  const steps = ai(e, 'steps', 2)
  const stepSpeed = ai(e, 'stepSpeed', 430)
  const stepTime = ai(e, 'stepTime', 0.18)
  const stepGap = ai(e, 'stepGap', 0.15)
  const recover = ai(e, 'recover', 0.45)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy)
  localSep(run, e, sepDir)

  if (e.state === K_APPROACH) {
    pursue(run, e, brainDir)
    const sp = e.speed * speedMul
    moveEnemy(run, e, (brainDir.x + sepDir.x * 1.4) * sp, (brainDir.y + sepDir.y * 1.4) * sp, dt)
    if (dist < lungeRange && e.cool <= 0 && e.spawnGrace <= 0 && run.world.lineOfSight(e.pos.x, e.pos.y, px, py)) {
      e.state = K_WINDUP
      e.stateT = 0
      e.windup = windup
      e.telegraph = windup
      e.tags.glint = true
      run.sfx('telegraph')
    }
    return
  }

  if (e.state === K_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, -dx * 0.22 + sepDir.x * 30, -dy * 0.22 + sepDir.y * 30, dt)
    if (Math.abs(dx) > 4) e.facing = dx < 0 ? -1 : 1
    if (e.windup <= 0) {
      e.tags.glint = false
      e.state = K_STEP
      e.tags.stepsDone = 0
      e.tags.gapT = 0
      courtierLock(run, e)
    }
    return
  }

  if (e.state === K_STEP) {
    const gap = num(e.tags.gapT, 0)
    if (gap > 0) {
      e.tags.gapT = gap - dt
      moveEnemy(run, e, 0, 0, dt)
      if (gap - dt <= 0) courtierLock(run, e)
      return
    }
    moveEnemy(run, e, num(e.tags.lx, 1) * stepSpeed, num(e.tags.ly, 0) * stepSpeed, dt)
    if (e.stateT > stepTime) {
      courtierStrike(run, e)
      const done = num(e.tags.stepsDone, 0) + 1
      e.tags.stepsDone = done
      if (done >= steps) {
        e.state = K_RECOVER
        e.stateT = 0
      } else {
        e.tags.gapT = stepGap
      }
    }
    return
  }

  moveEnemy(run, e, sepDir.x * 40, sepDir.y * 40, dt)
  if (e.stateT > recover) {
    e.state = K_APPROACH
    e.stateT = 0
    e.cool = cd
  }
})

// --- Digested Wailer: expanding rings of grief -------------------------------

const W_HOLD = 0
const W_WINDUP = 1
const W_CAST = 2
const W_RECOVER = 3

function fireRing(run: Run, e: EnemyEnt, index: number): void {
  const shards = Math.max(3, Math.round(ai(e, 'shards', 9)))
  const projId = typeof e.def.ai.projectile === 'string' ? e.def.ai.projectile : ''
  if (!projId) return
  // ring 2 offset by half a gap: solid near the body, gapped further out
  const off = (index % 2) * (Math.PI / shards)
  for (let i = 0; i < shards; i++) {
    const a = off + (i / shards) * TAU
    run.spawnProjectileAt(projId, e.pos.x, e.pos.y - 4, Math.cos(a), Math.sin(a), false)
  }
  run.sfx('shoot')
  emit(e.pos.x, e.pos.y - 4, { count: 12, color: '#a8e878', speed: 110, life: 0.35, size: 3, shape: 1 })
}

registerEnemyBrain('z6-wailer', (run, e, dt, speedMul) => {
  const keep = ai(e, 'keepRange', 250)
  const minRange = ai(e, 'minRange', 150)
  const fireRange = ai(e, 'fireRange', 330)
  const cd = ai(e, 'fireCooldown', 4.2)
  const windup = ai(e, 'windup', 0.58)
  const rings = Math.max(1, Math.round(ai(e, 'rings', 2)))
  const ringGap = ai(e, 'ringGap', 0.5)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  localSep(run, e, sepDir)

  if (e.state === W_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, sepDir.x * 20, sepDir.y * 20, dt)
    if (e.windup <= 0) {
      e.state = W_CAST
      e.stateT = 0
      e.tags.ringsLeft = rings - 1
      e.tags.ringT = ringGap
      fireRing(run, e, 0)
    }
    return
  }

  if (e.state === W_CAST) {
    moveEnemy(run, e, sepDir.x * 20, sepDir.y * 20, dt)
    const left = num(e.tags.ringsLeft, 0)
    const t = num(e.tags.ringT, 0) - dt
    e.tags.ringT = t
    if (t <= 0) {
      if (left > 0) {
        fireRing(run, e, rings - left)
        e.tags.ringsLeft = left - 1
        e.tags.ringT = ringGap
      } else {
        e.state = W_RECOVER
        e.stateT = 0
        e.cool = cd
      }
    }
    return
  }

  if (e.state === W_RECOVER) {
    moveEnemy(run, e, sepDir.x * 26, sepDir.y * 26, dt)
    if (e.stateT > 0.4) {
      e.state = W_HOLD
      e.stateT = 0
    }
    return
  }

  const los = run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
  const sp = e.speed * speedMul
  let mx = 0
  let my = 0
  if (dist < minRange) {
    mx = -(dx / dist)
    my = -(dy / dist)
  } else if (dist > keep || !los) {
    pursue(run, e, brainDir)
    mx = brainDir.x
    my = brainDir.y
  } else {
    const s = e.bob * 0.6 + e.pos.y * 0.01
    mx = (-dy / dist) * Math.sin(s)
    my = (dx / dist) * Math.sin(s)
  }
  moveEnemy(run, e, (mx + sepDir.x * 1.2) * sp, (my + sepDir.y * 1.2) * sp, dt)
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1

  if (e.cool <= 0 && e.spawnGrace <= 0 && dist < fireRange && los) {
    e.state = W_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- Digested Gravetender: it is not here for you ----------------------------

let tenderSeq = 0

registerEnemyBrain('z6-gravetender', (run, e, dt, speedMul) => {
  const sprintMul = ai(e, 'sprintMul', 1.55)
  const tendRange = PALE.tendRange
  const bandMin = ai(e, 'bandMin', 190)
  const bandMax = ai(e, 'bandMax', 285)
  const claimPenalty = ai(e, 'claimPenalty', 420)

  if (typeof e.tags.tid !== 'number') e.tags.tid = ++tenderSeq
  const tid = e.tags.tid as number

  let best: EnemyEnt | null = null
  let bestScore = Infinity
  const list = run.enemyPool.live
  for (let i = 0; i < list.length; i++) {
    const o = list[i]
    if (!o.alive || o.def.id !== PALE.huskId || o.tags.tended === true) continue
    let sc = Math.hypot(o.pos.x - e.pos.x, o.pos.y - e.pos.y)
    if (num(o.tags.tendClaimT, -1) > run.time && o.tags.tendBy !== tid) sc += claimPenalty
    if (sc < bestScore) {
      bestScore = sc
      best = o
    }
  }

  localSep(run, e, sepDir)

  if (!best) {
    e.tags.chanT = 0
    e.tags.hasWork = false
    const dx = run.player.pos.x - e.pos.x
    const dy = run.player.pos.y - e.pos.y
    const d = Math.hypot(dx, dy) || 1
    const sp = e.speed * speedMul
    let mx = 0
    let my = 0
    if (d < bandMin) {
      mx = -(dx / d)
      my = -(dy / d)
    } else if (d > bandMax) {
      pursue(run, e, brainDir)
      mx = brainDir.x
      my = brainDir.y
    } else {
      const s = e.bob * 0.5
      mx = (-dy / d) * Math.sin(s)
      my = (dx / d) * Math.sin(s)
    }
    moveEnemy(run, e, (mx + sepDir.x) * sp, (my + sepDir.y) * sp, dt)
    if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1
    return
  }

  e.tags.hasWork = true
  e.tags.tx = best.pos.x
  e.tags.ty = best.pos.y

  const dx = best.pos.x - e.pos.x
  const dy = best.pos.y - e.pos.y
  const d = Math.hypot(dx, dy) || 1

  if (d > tendRange + e.radius) {
    e.tags.chanT = 0
    const sp = e.speed * sprintMul * speedMul
    moveEnemy(run, e, (dx / d + sepDir.x * 0.6) * sp, (dy / d + sepDir.y * 0.6) * sp, dt)
    e.facing = dx < 0 ? -1 : 1
    return
  }

  let c = num(e.tags.chanT, 0)
  if (c <= 0) {
    c = PALE.tendChannel
    run.sfx('telegraph')
  }
  c -= dt
  e.tags.chanT = c
  e.telegraph = Math.max(e.telegraph, Math.min(0.6, Math.max(0, c)))
  best.tags.tendClaimT = run.time + 0.25
  best.tags.tendBy = tid
  moveEnemy(run, e, 0, 0, dt)

  if (c <= 0) {
    e.tags.chanT = 0
    best.tags.tended = true
    const t = Math.min(num(best.tags.huskT, 0), PALE.tendedSeconds)
    best.tags.huskT = t
    best.tags.huskMax = Math.max(t, 0.001)
    e.cool = 0.4
    run.sfx('door')
    run.shake(2, 0.2)
    emit(best.pos.x, best.pos.y, { count: 16, color: C.tend, speed: 130, life: 0.5, lifeVar: 0.2, size: 3, shape: 1 })
  }
})

// --- Swallowed Delver: the grunt that leaves the floor -----------------------

const D_APPROACH = 0
const D_WINDUP = 1
const D_STRIKE = 2
const D_RECOVER = 3
const D_SINK = 4
const D_PLUME = 5
const D_ERUPT = 6

registerEnemyBrain('z6-delver', (run, e, dt, speedMul) => {
  const reach = ai(e, 'attackRange', 28)
  const cd = ai(e, 'attackCooldown', 1.9)
  const windup = ai(e, 'windup', 0.45)
  const strikeRange = ai(e, 'strikeRange', 44)
  const strikeDmg = ai(e, 'strikeDamage', 1.2)
  const burrowCd = ai(e, 'burrowCooldown', 7.5)
  const burrowMin = ai(e, 'burrowMinRange', 90)
  const burrowMax = ai(e, 'burrowMaxRange', 330)
  const sinkT = ai(e, 'sinkSeconds', 0.45)
  const underT = ai(e, 'burrowSeconds', 2.5)
  const plumeSpeed = ai(e, 'plumeSpeed', 76)
  const eruptWind = ai(e, 'eruptWindup', 0.5)
  const eruptR = ai(e, 'eruptRadius', 54)
  const eruptDmg = ai(e, 'eruptDamage', 1.5)
  const eruptRec = ai(e, 'eruptRecover', 0.7)

  if (typeof e.tags.z6touch !== 'number') e.tags.z6touch = e.touchDamage

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1

  e.tags.z6bcd = Math.max(0, num(e.tags.z6bcd, burrowCd * 0.5) - dt)

  if (e.state === D_SINK) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT >= sinkT) {
      e.state = D_PLUME
      e.stateT = 0
      e.spriteId = 'z6_plume'
      e.tags.invulnerable = true
      e.tags.z6under = true
      e.touchDamage = 0
      run.sfx('door')
    }
    return
  }

  if (e.state === D_PLUME) {
    pursue(run, e, brainDir)
    moveEnemy(run, e, brainDir.x * plumeSpeed, brainDir.y * plumeSpeed, dt)
    if (run.rngObj.chance(0.4)) {
      emit(e.pos.x, e.pos.y + 6, { count: 2, color: C.fleshHi, speed: 55, life: 0.4, size: 3, grav: 140 })
    }
    if (e.stateT >= underT) {
      e.state = D_ERUPT
      e.stateT = 0
      e.windup = eruptWind
      e.telegraph = eruptWind
      e.tags.z6eruptR = eruptR
      run.sfx('telegraph')
    }
    return
  }

  if (e.state === D_ERUPT) {
    moveEnemy(run, e, 0, 0, dt)
    e.windup -= dt
    if (e.windup <= 0) {
      e.spriteId = e.def.id
      e.tags.invulnerable = false
      e.tags.z6under = false
      e.touchDamage = num(e.tags.z6touch, e.touchDamage)
      e.tags.z6eruptR = 0
      e.state = D_RECOVER
      e.stateT = 0
      e.cool = eruptRec
      e.tags.z6bcd = burrowCd
      if (dist <= eruptR + run.player.radius) run.damagePlayer(e.touchDamage * eruptDmg, e.def.id)
      run.sfx('generator-die')
      run.shake(4, 0.28)
      emit(e.pos.x, e.pos.y, {
        count: 24,
        color: C.fleshHi,
        speed: 260,
        speedVar: 180,
        life: 0.55,
        lifeVar: 0.25,
        size: 4,
        grav: 320,
        shape: 1,
      })
    }
    return
  }

  if (e.state === D_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, 0, 0, dt)
    if (e.windup <= 0) {
      e.state = D_STRIKE
      e.stateT = 0
      if (dist <= strikeRange + run.player.radius + e.radius) run.damagePlayer(e.touchDamage * strikeDmg, e.def.id)
      run.sfx('swing')
    }
    return
  }

  if (e.state === D_STRIKE) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > 0.12) {
      e.state = D_RECOVER
      e.stateT = 0
    }
    return
  }

  if (e.state === D_RECOVER) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > 0.4) {
      e.state = D_APPROACH
      e.stateT = 0
      e.cool = Math.max(e.cool, cd)
    }
    return
  }

  pursue(run, e, brainDir)
  const sp = e.speed * speedMul
  moveEnemy(run, e, brainDir.x * sp, brainDir.y * sp, dt)
  if (e.spawnGrace > 0) return
  if (dist < reach + run.player.radius + e.radius && e.cool <= 0) {
    e.state = D_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
    return
  }
  if (e.tags.z6bcd === 0 && dist > burrowMin && dist < burrowMax) {
    e.state = D_SINK
    e.stateT = 0
    e.tags.z6bcd = burrowCd
    run.sfx('dash')
  }
})

// --- Gloommoth: the swarm that will not come into the light ------------------
// Zone 4's matched pair kept intact: it flees BRIGHTNESS above a threshold, not
// the light's radius, so the dim rim is a band it will cross and a room holding
// one can always be cleared.

registerEnemyBrain('z6-moth', (run, e, dt, speedMul) => {
  const lungeRange = ai(e, 'lungeRange', 130)
  const lungeCd = ai(e, 'lungeCooldown', 1.6)
  const windup = ai(e, 'windup', 0.26)
  const lungeSpeed = ai(e, 'lungeSpeed', 380)
  const lungeTime = ai(e, 'lungeTime', 0.22)
  const dazeTime = ai(e, 'dazeSeconds', 0.45)
  const fleeMul = ai(e, 'fleeSpeedMul', 1.35)
  const fleeAbove = ai(e, 'fleeAbove', 0.75)
  const spiral = ai(e, 'spiral', 0.85)

  const passive = (run.relicFlags.glintmothPassive ?? 0) > 0
  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const sp = e.speed * speedMul

  if (e.state === 1) {
    e.windup -= dt
    moveEnemy(run, e, -dx * 0.2, -dy * 0.2, dt)
    if (e.windup <= 0) {
      e.state = 2
      e.stateT = 0
      e.kbx = (dx / dist) * lungeSpeed
      e.kby = (dy / dist) * lungeSpeed
    }
    return
  }
  if (e.state === 2) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > lungeTime) {
      e.state = 3
      e.stateT = 0
    }
    return
  }
  if (e.state === 3) {
    e.tags.z6scared = 0
    moveEnemy(run, e, Math.cos(e.bob * 9) * 18, Math.sin(e.bob * 11) * 18, dt)
    if (e.stateT > dazeTime) {
      e.state = 0
      e.stateT = 0
      e.cool = lungeCd
    }
    return
  }

  // the light rule, but only where there is a dark to be afraid of
  if (zone6Lights.dark) {
    const lit = zone6Lights.lightAt(e.pos.x, e.pos.y)
    if (lit > fleeAbove) {
      zone6Lights.nearestSource(e.pos.x, e.pos.y, srcScratch)
      const ld = Math.hypot(e.pos.x - srcScratch.x, e.pos.y - srcScratch.y)
      const ox = ld > 0.5 ? (e.pos.x - srcScratch.x) / ld : 1
      const oy = ld > 0.5 ? (e.pos.y - srcScratch.y) / ld : 0
      const push = Math.min(1, (lit - fleeAbove) / Math.max(0.05, 1 - fleeAbove))
      const s = Math.sin(e.bob * 2.4 + e.pos.x * 0.02)
      moveEnemy(run, e, (ox * push * fleeMul - oy * s * 0.8) * sp, (oy * push * fleeMul + ox * s * 0.8) * sp, dt)
      e.tags.z6scared = 1
      return
    }
  }
  e.tags.z6scared = 0

  if (passive) {
    moveEnemy(run, e, Math.cos(e.bob * 0.7) * sp * 0.4, Math.sin(e.bob * 1.3) * sp * 0.4, dt)
    return
  }

  pursue(run, e, brainDir)
  const s = Math.sin(e.bob * 1.9) * spiral
  moveEnemy(run, e, (brainDir.x - brainDir.y * s) * sp, (brainDir.y + brainDir.x * s) * sp, dt)
  if (dist < lungeRange && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = 1
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- the husk: a countdown with a hitbox -------------------------------------

registerEnemyBrain('z6-husk', (run, e, dt) => {
  e.vel.x = 0
  e.vel.y = 0

  // Sexton's Spade (relic flag): walking over a husk seals it, no input.
  if ((run.relicFlags.huskAutoSealOnWalk ?? 0) > 0) {
    const dx = run.player.pos.x - e.pos.x
    const dy = run.player.pos.y - e.pos.y
    const reach = e.radius + run.player.radius + PALE.walkSealSlack
    if (dx * dx + dy * dy <= reach * reach) {
      e.kill() // non-silent: routes through onEnemyDeath, which is the seal
      return
    }
  }

  const t = num(e.tags.huskT, 0) - dt
  e.tags.huskT = t
  if (t > 0) return
  reformHusk(run, e)
})

/** The timer ran out: the body comes back at full health, once. */
function reformHusk(run: Run, e: EnemyEnt): void {
  const srcId = typeof e.tags.srcId === 'string' ? e.tags.srcId : ''
  const tended = e.tags.tended === true
  const carried = typeof e.tags.srcElite === 'string' && e.tags.srcElite ? (e.tags.srcElite as EliteRider) : null
  const elite: EliteRider | null = tended ? 'frenzied' : carried
  posScratch.x = e.pos.x
  posScratch.y = e.pos.y

  e.kill(true) // silent: a husk's own expiry is not a kill and leaves no husk

  if (srcId) {
    const twin = srcId + PALE.reformedSuffix
    const id = run.enemyDef(twin) ? twin : srcId
    const born = run.spawnEnemy(id, posScratch, { elite, silent: true })
    if (born.alive) born.tags.reformed = true
  }

  // DESIGN §5 Zone 3, carried down: sloppiness darkens the zone — and in the
  // Gloomheart the zone never resets, so it darkens the whole run through here.
  run.gloom.add(PALE.reformGloom)
  run.sfx('door')
  run.shake(2.5, 0.22)
  emit(posScratch.x, posScratch.y, {
    count: 20,
    color: tended ? C.tend : C.huskGlow,
    speed: 200,
    speedVar: 140,
    life: 0.5,
    lifeVar: 0.2,
    size: 4,
    shape: 1,
  })
  mech.noteReform()
}

// --- The Unlit ---------------------------------------------------------------
// The brain is only the body: rise, walk, halt at the edge of brazier light,
// recoil, sink. Every policy question belongs to the mechanic.

const UN_RISE = 0
const UN_WALK = 1
const UN_RECOIL = 2
const UN_SINK = 3

registerEnemyBrain('z6-unlit', (run, e, dt) => {
  const mode = num(e.tags.z6mode, UN_RISE)
  const t = num(e.tags.z6t, 0) + dt
  e.tags.z6t = t

  // D-031: first steel that lands says the counterplay, in place (see the
  // zone-4 twin — same rule, same reason: an unexplained ping reads as a bug).
  if (e.hitFlashT > 0 && e.tags.saidSteel !== true) {
    e.tags.saidSteel = true
    run.bark('Your steel passes through it. Brazier-light halts the Unlit; an Emberburst banishes it.')
  }

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1

  if (mode === UN_RISE) {
    e.touchDamage = 0
    moveEnemy(run, e, 0, 0, dt)
    if (t >= U.riseSeconds) {
      e.tags.z6mode = UN_WALK
      e.tags.z6t = 0
      e.touchDamage = U.touchDamage
    }
    return
  }

  if (mode === UN_SINK) {
    e.touchDamage = 0
    moveEnemy(run, e, 0, 0, dt)
    if (t >= U.sinkSeconds) e.kill(true)
    return
  }

  if (mode === UN_RECOIL) {
    e.touchDamage = 0
    moveEnemy(run, e, 0, 0, dt)
    if (t >= U.recoilSeconds) {
      e.tags.z6mode = UN_WALK
      e.tags.z6t = 0
      e.touchDamage = U.touchDamage
    }
    return
  }

  e.touchDamage = U.touchDamage
  pursue(run, e, brainDir)
  const ahead = e.radius + 8
  if (
    zone6Lights.inBrazierLight(e.pos.x + brainDir.x * ahead, e.pos.y + brainDir.y * ahead) ||
    zone6Lights.inBrazierLight(e.pos.x, e.pos.y)
  ) {
    e.tags.z6halt = 1
    moveEnemy(run, e, -brainDir.x * 26, -brainDir.y * 26, dt)
    return
  }
  e.tags.z6halt = 0
  moveEnemy(run, e, brainDir.x * e.speed, brainDir.y * e.speed, dt)

  // The touch is dealt here rather than by the shared contact pass: the recoil
  // has to zero touchDamage in the same step, and contact damage runs after the
  // brains (zone 4's lesson, kept).
  if (dist < e.radius + run.player.radius + 2) {
    run.damagePlayer(U.touchDamage, e.def.id)
    e.tags.z6mode = UN_RECOIL
    e.tags.z6t = 0
    e.touchDamage = 0
    e.kbx = -(dx / dist) * (U.recoilDistance / U.recoilSeconds) * 3.2
    e.kby = -(dy / dist) * (U.recoilDistance / U.recoilSeconds) * 3.2
    run.sfx('player-hurt')
    run.shake(6, 0.4)
    emit(e.pos.x, e.pos.y, { count: 18, color: '#120c20', speed: 200, life: 0.5, size: 4, shape: 1 })
  }
})

// ===========================================================================
// 5. THE MECHANIC
// ===========================================================================

type Mode = 'hub' | 'drown' | 'pale' | 'dark' | 'arena'
type SurgePhase = 'calm' | 'telegraph' | 'flood'

const viewR = { x: 0, y: 0, w: 0, h: 0, zoom: 1 }
const probeA: Vec = { x: 0, y: 0 }
const probeB: Vec = { x: 100, y: 0 }
const textAt: Vec = { x: 0, y: 0 }

function viewRect(d: Draw): void {
  const a = d.worldToScreen(probeA)
  const ax = a.x
  const b = d.worldToScreen(probeB)
  const zoom = (b.x - ax) / 100 || 1
  viewR.zoom = zoom
  viewR.x = d.camera.x - TILE * 3
  viewR.y = d.camera.y - TILE * 3
  viewR.w = LOGICAL_W / zoom + TILE * 6
  viewR.h = LOGICAL_H / zoom + TILE * 6
}

const MODE_BARK: Record<Mode, string> = {
  hub: 'The Heartchamber. Three gullets, three seals — and the meter does not stop.',
  drown: 'It remembers the water. Faster than the water did.',
  pale: 'It remembers the court. Two seconds, and they are up again.',
  dark: 'It remembers the dark. Something is already walking.',
  arena: '',
}

/** Coming back up a gullet with a seal in hand. The runner counts; this says it. */
const SEAL_BARK = [
  'The Deep yields a seal. Two gullets still breathing.',
  'The Deep yields a second seal — and the clock has not once looked away.',
  'Three. The Deep yields the last seal, and the heart-gate remembers you.',
]

class Gloomheart implements ZoneMechanic {
  readonly zoneId = 6 as const

  private ctx: RunCtx | null = null
  private anim = 0
  private mode: Mode = 'hub'

  // --- the surge (drowning spur)
  private surge: SurgePhase = 'calm'
  private surgeT = 0
  private surges = 0
  private readonly floodAcc: Z6FloodAcc = { dmg: 0, push: 0 }
  private rumbleAcc = 0
  private toldTheTide = false

  // --- husks (pale spur, and the Sovereign's Memory of the Court)
  private huskCount = 0
  private sealedThisRoom = 0
  private reformedThisRoom = 0
  private candleReady = false
  private saidHusk = false

  // --- light (dark spur, and the Memory of the Dark)
  private channel = -1
  private channelT = 0
  private unlit: Enemy | null = null
  private unlitTimer = 0
  private banishT = 0
  private droneT = 0
  private lastPotions = 0
  private mask: HTMLCanvasElement | null = null
  private maskCtx: CanvasRenderingContext2D | null = null

  // --- the zone's own meter, remembered for the pulse
  private gloomSeen = 0
  /** Seals taken, counted by watching the rooms go past — the runner owns the truth. */
  private sealsSaid = 0
  private cameFromSpur = false

  // ------------------------------------------------------------- lifecycle

  onZoneStart(ctx: RunCtx): void {
    this.ctx = ctx
    this.anim = 0
    this.toldTheTide = false
    this.saidHusk = false
    this.sealsSaid = 0
    this.cameFromSpur = false
    tenderSeq = 0
    zone6Arena.active = false
    zone6Arena.bands.length = 0
    zone6Arena.husks = false
    zone6Arena.dark = false
    zone6Arena.recoil = 0
    zone6Arena.sovereign.on = false
    zone6Arena.heart.on = false
    for (const l of zone6Arena.lanes) l.on = false

    // THE ZONE'S ONE NEW NUMBER (DESIGN §5 Zone 6): the meter opens at 40% and
    // never resets inside this zone — the per-zone reset already happened, and
    // nothing here ever subtracts.
    ctx.gloom.add(ZONE6_GLOOM.openAt)
    this.gloomSeen = ctx.gloom.value
    ctx.bark(ZONE6_GLOOM.openBark)
    ctx.shake(4, 0.6)
    ctx.sfx('door')
  }

  onRoomStart(ctx: RunCtx, room: Room): void {
    this.ctx = ctx
    const id = room.template.id
    this.mode =
      id === ZONE6_BOSS_ARENA.id
        ? 'arena'
        : id === 'z6-drowning-spur'
          ? 'drown'
          : id === 'z6-pale-spur'
            ? 'pale'
            : id === 'z6-dark-spur'
              ? 'dark'
              : 'hub'

    this.buildRoom(ctx, room)

    this.surge = 'calm'
    this.surgeT = SURGE.firstDelay
    this.surges = 0
    this.floodAcc.dmg = 0
    this.floodAcc.push = 0
    this.rumbleAcc = 0
    this.huskCount = 0
    this.sealedThisRoom = 0
    this.reformedThisRoom = 0
    this.candleReady = (ctx as unknown as RunInternals).flags?.graveCandle === true
    this.channel = -1
    this.channelT = 0
    this.unlit = null
    this.unlitTimer = U.spawnAtSeconds
    this.banishT = 0
    this.droneT = 0
    this.lastPotions = ctx.player.potions
    ctx.player.zoneSpeedMul = 1

    // The dark is the dark spur's, and the Sovereign's third memory. Nowhere else.
    zone6Lights.dark = this.mode === 'dark'
    zone6Arena.active = this.mode === 'arena'
    if (this.mode !== 'arena') {
      zone6Arena.husks = false
      zone6Arena.dark = false
      zone6Arena.sovereign.on = false
      zone6Arena.heart.on = false
      zone6Arena.bands.length = 0
      for (const l of zone6Arena.lanes) l.on = false
    }
    // The dark spur opens lit: two braziers, burning, and 20 seconds of grace.
    if (this.mode === 'dark') {
      for (let i = 0; i < zone6Lights.braziers.length; i++) z6LightBrazier(i, BR.burnSeconds)
    }

    // Coming back up out of a gullet: the runner has just paid the seal, so this
    // is the moment to say what it bought and what is left. Otherwise the room
    // introduces itself, once.
    if (this.mode === 'hub' && this.cameFromSpur) {
      ctx.bark(SEAL_BARK[Math.min(SEAL_BARK.length - 1, this.sealsSaid)])
      this.sealsSaid++
      ctx.sfx('pickup')
    } else {
      const line = MODE_BARK[this.mode]
      if (line) ctx.bark(line)
    }
    this.cameFromSpur = this.mode === 'drown' || this.mode === 'pale' || this.mode === 'dark'
  }

  onZoneEnd(ctx: RunCtx): void {
    ctx.player.lampRadius = 0
    zone6Lights.braziers.length = 0
    zone6Lights.dark = false
    zone6Arena.active = false
    zone6Arena.bands.length = 0
    zone6Arena.dark = false
    zone6Arena.husks = false
    zone6Arena.sovereign.on = false
    zone6Arena.heart.on = false
    zone6Arena.recoil = 0
    floorLayer = null
    waterTiles = []
    tiles = new Uint8Array(0)
    gw = 0
    gh = 0
    this.unlit = null
    this.ctx = null
  }

  // ------------------------------------------------------------- room build
  // findGlyphTiles allocates, so it is called a fixed number of times per room
  // and the results are cached for the frame loop.

  private buildRoom(ctx: RunCtx, room: Room): void {
    const map = room.template.map
    gh = map.length
    gw = map[0]?.length ?? 0
    if (tiles.length !== gw * gh) tiles = new Uint8Array(gw * gh)
    tiles.fill(T_OTHER)

    waterTiles = ctx.findGlyphTiles('W')
    for (const p of waterTiles) {
      const tx = Math.floor(p.x / TILE)
      const ty = Math.floor(p.y / TILE)
      if (tx >= 0 && ty >= 0 && tx < gw && ty < gh) tiles[ty * gw + tx] = T_WATER
    }
    for (const p of ctx.findGlyphTiles('R')) {
      const tx = Math.floor(p.x / TILE)
      const ty = Math.floor(p.y / TILE)
      if (tx >= 0 && ty >= 0 && tx < gw && ty < gh) tiles[ty * gw + tx] = T_STONE
    }

    const l = Math.hypot(DROWN.current.x, DROWN.current.y) || 1
    current.x = DROWN.current.x / l
    current.y = DROWN.current.y / l

    zone6Lights.braziers.length = 0
    for (const t of ctx.findGlyphTiles('B')) {
      zone6Lights.braziers.push({
        x: t.x,
        y: t.y,
        tx: Math.floor(t.x / TILE),
        ty: Math.floor(t.y / TILE),
        lit: false,
        fuel: 0,
        flare: 0,
        smoke: 0,
      })
    }

    floorLayer = paintFloorLayer(ctx, this.mode)
    for (const r of ripples) r.t = 0
  }

  // ---------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.ctx = ctx
    this.anim += dt
    this.gloomSeen = ctx.gloom.value
    if (zone6Arena.recoil > 0) zone6Arena.recoil = Math.max(0, zone6Arena.recoil - dt * 0.8)
    for (let i = 0; i < ripples.length; i++) if (ripples[i].t > 0) ripples[i].t -= dt

    // the water tax — recomputed from 1 every step, never accumulated
    const wet = ctx.tileAt(ctx.player.pos) === 'W'
    if (wet && (this.mode === 'drown' || this.mode === 'arena')) {
      const immune = (relicFlags(ctx).waterSpeedImmunity ?? 0) > 0
      const flooding = this.surge === 'flood' || this.arenaFlooding()
      ctx.player.zoneSpeedMul = immune ? 1 : flooding ? DROWN.floodSlowMul : DROWN.slowMul
    }

    // the light, wherever there is a dark
    if (zone6Lights.dark || this.mode === 'arena') {
      const lampMul = metaFlag(ctx, 'lampRadiusMul') * (relicFlags(ctx).lampRadiusMul ?? 1)
      const lampR = DARK.lampRadius * lampMul
      ctx.player.lampRadius = zone6Lights.dark ? lampR : 0
      zone6Lights.lampRadius = lampR
      zone6Lights.lampX = ctx.player.pos.x
      zone6Lights.lampY = ctx.player.pos.y
      this.updateBraziers(ctx, dt)
    }

    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) if (list[i].alive && list[i].def.id === PALE.huskId) n++
    this.huskCount = n

    if (this.mode === 'drown') this.updateSurge(ctx, dt, wet)
    if (this.mode === 'dark') this.updateUnlit(ctx, dt)
    if (this.mode === 'arena') zone6Lights.dark = zone6Arena.dark
  }

  private arenaFlooding(): boolean {
    for (let i = 0; i < zone6Arena.bands.length; i++) if (zone6Arena.bands[i].state === -1) return true
    return false
  }

  /**
   * The beat. calm -> telegraph (klaxon, shimmer, a rising water line) -> flood
   * (15 dmg/s and a current) -> calm, every 18 seconds. Fixed period, so a
   * player who counts can plan a crossing — and the causeway is broken, so
   * everybody has to cross.
   */
  private updateSurge(ctx: RunCtx, dt: number, wet: boolean): void {
    this.surgeT -= dt

    if (this.surge === 'calm') {
      if (this.surgeT <= 0) {
        this.surge = 'telegraph'
        this.surgeT = SURGE.telegraph
        this.surges++
        ctx.sfx('door')
        ctx.sfx('telegraph')
        ctx.shake(1.6, 0.5)
        if (!this.toldTheTide) {
          this.toldTheTide = true
          ctx.bark('The klaxon, out of a wall that is breathing. Eighteen seconds, not twenty-five.')
        }
      }
      return
    }

    if (this.surge === 'telegraph') {
      this.rumbleAcc -= dt
      if (this.rumbleAcc <= 0) {
        this.rumbleAcc = 0.75
        ctx.sfx('telegraph')
        ctx.shake(1.1, 0.3)
      }
      if (this.surgeT <= 0) {
        this.surge = 'flood'
        this.surgeT = SURGE.duration
        this.floodAcc.dmg = 0
        this.floodAcc.push = 0
        ctx.sfx('generator-die')
        ctx.shake(7, 0.6)
        ctx.hitstop(0.05)
        this.spray(ctx)
      }
      return
    }

    ctx.shake(SURGE.shake, 0.15)
    z6FloodHurt(ctx, dt, wet, this.floodAcc, current.x, current.y, SURGE)
    if (this.surgeT <= 0) {
      this.surge = 'calm'
      this.surgeT = Math.max(2, SURGE.period - SURGE.telegraph - SURGE.duration)
      ctx.sfx('door')
    }
  }

  private spray(ctx: RunCtx): void {
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y
    for (let i = 0; i < 5; i++) {
      const t = waterTiles[Math.floor(ctx.rng() * waterTiles.length) % Math.max(1, waterTiles.length)]
      if (!t) break
      if (Math.abs(t.x - px) > 640 || Math.abs(t.y - py) > 400) continue
      emit(t.x, t.y, {
        count: 10,
        color: C.floodHot,
        speed: 220,
        speedVar: 160,
        life: 0.5,
        lifeVar: 0.2,
        size: 4,
        grav: 300,
        shape: 1,
      })
    }
  }

  // ------------------------------------------------------------- the fires

  private updateBraziers(ctx: RunCtx, dt: number): void {
    const list = zone6Lights.braziers
    for (let i = 0; i < list.length; i++) {
      const b = list[i]
      if (b.flare > 0) b.flare -= dt
      if (b.smoke > 0) b.smoke -= dt
      if (!b.lit) continue
      b.fuel -= dt
      if (b.fuel <= 0) {
        z6DouseBrazier(i)
        ctx.sfx('door')
        continue
      }
      if (ctx.rng() < (b.fuel < BR.gutterSeconds ? 0.5 : 0.18)) {
        emit(b.x, b.y - 12, {
          count: 1,
          color: b.fuel < BR.gutterSeconds ? C.emberDead : C.ember,
          speed: 26,
          life: 0.8,
          size: 2,
          grav: -30,
          shape: 1,
        })
      }
    }
  }

  /** E on a cold brazier. A flick here — the Sovereign is pressure enough. */
  onInteract(ctx: RunCtx): boolean {
    if (!zone6Lights.dark && this.mode !== 'arena') return false
    const i = zone6Lights.nearestUnlit(ctx.player.pos.x, ctx.player.pos.y, BR.reach)
    if (i < 0) return false
    z6LightBrazier(i, BR.burnSeconds)
    ctx.sfx('potion')
    ctx.sfx('pickup')
    ctx.shake(1.6, 0.2)
    ctx.bark('Light, in a place that has never had any.')
    return true
  }

  // ------------------------------------------------------------- the Unlit

  private updateUnlit(ctx: RunCtx, dt: number): void {
    const potions = ctx.player.potions
    const quaffed = potions < this.lastPotions
    this.lastPotions = potions

    const live = this.unlit != null && this.unlit.alive
    if (live && quaffed) {
      const u = this.unlit as Enemy
      if (Math.hypot(u.pos.x - ctx.player.pos.x, u.pos.y - ctx.player.pos.y) <= U.banishRadius) this.banish(ctx, u)
    }

    if (this.banishT > 0) {
      this.banishT -= dt
      if (this.banishT <= 0 && !ctx.room.cleared) this.spawnUnlit(ctx, true)
      return
    }

    if (!live) {
      this.unlit = null
      if (ctx.room.cleared) return
      this.unlitTimer -= dt
      if (this.unlitTimer <= 0) this.spawnUnlit(ctx, false)
      return
    }

    // the drone: the cue's RATE carries the distance (engine/audio.ts has no
    // continuous voice, and this file may not add one).
    const u = this.unlit as Enemy
    const d = Math.hypot(u.pos.x - ctx.player.pos.x, u.pos.y - ctx.player.pos.y)
    const k = Math.max(0, Math.min(1, 1 - d / U.droneRange))
    this.droneT -= dt
    if (this.droneT <= 0) {
      this.droneT = U.droneFar + (U.droneNear - U.droneFar) * k
      ctx.sfx('door')
    }
  }

  private spawnUnlit(ctx: RunCtx, returning: boolean): void {
    if (gw === 0) return
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y
    let bx = -1
    let by = -1
    let bs = -1
    for (let i = 0; i < 220; i++) {
      const tx = 1 + Math.floor(ctx.rng() * (gw - 2))
      const ty = 1 + Math.floor(ctx.rng() * (gh - 2))
      if (ctx.isWall(tx, ty)) continue
      const x = tx * TILE + TILE / 2
      const y = ty * TILE + TILE / 2
      const d = Math.hypot(x - px, y - py)
      if (d < U.spawnMinDistance) continue
      const score = d - zone6Lights.lightAt(x, y) * 900
      if (score > bs) {
        bs = score
        bx = x
        by = y
      }
    }
    if (bx < 0) {
      this.unlitTimer = 6
      return
    }
    srcScratch.x = bx
    srcScratch.y = by
    const e = ctx.spawnEnemy(U.defId, srcScratch, { elite: null, silent: true })
    if (!e.alive) {
      this.unlitTimer = 6
      return
    }
    // The frozen tag contract. `gloomspawn` on a thing that is not gloomspawn is
    // zone 4's flagged reading, and it is the same reading here: that tag is the
    // engine's only "does not hold the room hostage" switch, and the Unlit
    // cannot be killed — without it the dark spur could never be cleared.
    e.tags.invulnerable = true
    e.tags.noKnockback = true
    e.tags.gloomspawn = true
    e.tags.z6unlit = true
    e.tags.z6mode = UN_RISE
    e.tags.z6t = 0
    this.unlit = e
    emit(bx, by, { count: 22, color: '#0d0a18', speed: 130, life: 0.8, size: 5, shape: 1 })
    ctx.sfx('death')
    ctx.shake(3, 0.5)
    ctx.bark(returning ? 'It is back. It was always going to be back.' : 'The Unlit walks here too. Of course it does.')
  }

  private banish(ctx: RunCtx, u: Enemy): void {
    u.tags.z6mode = UN_SINK
    u.tags.z6t = 0
    this.banishT = U.banishSeconds
    this.unlit = null
    ctx.featTick('unlitBanished')
    ctx.sfx('potion')
    ctx.shake(5, 0.4)
    emit(u.pos.x, u.pos.y, {
      count: 30,
      color: C.lampHot,
      speed: 240,
      speedVar: 160,
      life: 0.7,
      lifeVar: 0.3,
      size: 4,
      shape: 1,
    })
    ctx.bark('The burst puts it down. It has all the time in the world.')
  }

  // ------------------------------------------------------------- the husks

  /** Called by the husk timer path so the room counters stay honest. */
  noteReform(): void {
    this.reformedThisRoom++
  }

  private seal(ctx: RunCtx, x: number, y: number, tended: boolean): void {
    const bonus = relicFlags(ctx).huskSealEmberBonus ?? 0
    ctx.addEmbers(PALE.sealEmbers + bonus)
    ctx.featTick('huskSealed')
    ctx.sfx('pickup')
    this.sealedThisRoom++
    fx.floatText('SEALED', x, y - 20, C.huskGlow, 11)
    emit(x, y, { count: 14, color: C.huskGlow, speed: 150, speedVar: 90, life: 0.45, lifeVar: 0.2, size: 3, shape: 1 })
    emit(x, y, { count: 6, color: tended ? C.tend : C.huskCore, speed: 70, life: 0.6, size: 2, shape: 1 })
  }

  /**
   * The husk rule, in one hook.
   *   a husk died -> it was hit -> that is a seal (the timer path kills silently,
   *                 and silent deaths never reach this hook)
   *   a body died -> lay a husk, unless it was already a re-form
   *
   * Where it applies is the difference between the spur and the arena: the Pale
   * Spur runs it on its own roster; the Sovereign's Memory of the Court runs it
   * on every escort it has poured, whatever your build made it bring.
   */
  onEnemyDeath(ctx: RunCtx, e: Enemy): void {
    if (e.def.id === PALE.huskId) {
      this.seal(ctx, e.pos.x, e.pos.y, e.tags.tended === true)
      return
    }
    if (e.tags.reformed === true) return
    const arenaHusks = this.mode === 'arena' && zone6Arena.husks && !e.tags.gloomspawn && e.def.id !== 'z6-huskheart'
    const spurHusks = this.mode === 'pale' && PALE.huskSources.indexOf(e.def.id) >= 0
    if (!arenaHusks && !spurHusks) return
    if (this.candleReady) {
      this.candleReady = false
      this.seal(ctx, e.pos.x, e.pos.y, false)
      ctx.bark('The grave candle takes that one.')
      return
    }
    z6LayHusk(ctx, e.pos.x, e.pos.y, e.def.id, e.elite, PALE.huskSeconds)
    if (!this.saidHusk) {
      this.saidHusk = true
      ctx.bark('A husk, and two seconds on it. Seal it or fight it twice.')
    }
  }

  // ---------------------------------------------------------------- render

  /** Under the bodies: the flesh-stone floor, the water, and the tide. */
  renderUnder(d: Draw): void {
    if (gw === 0) return
    const ctx = d.ctx
    viewRect(d)

    if (floorLayer) {
      const sx = Math.max(0, Math.floor(viewR.x))
      const sy = Math.max(0, Math.floor(viewR.y))
      const sw = Math.min(floorLayer.width - sx, Math.ceil(viewR.w))
      const sh = Math.min(floorLayer.height - sy, Math.ceil(viewR.h))
      if (sw > 0 && sh > 0) ctx.drawImage(floorLayer, sx, sy, sw, sh, sx, sy, sw, sh)
    }

    const tx0 = Math.max(0, Math.floor(viewR.x / TILE))
    const ty0 = Math.max(0, Math.floor(viewR.y / TILE))
    const tx1 = Math.min(gw - 1, Math.ceil((viewR.x + viewR.w) / TILE))
    const ty1 = Math.min(gh - 1, Math.ceil((viewR.y + viewR.h) / TILE))

    this.renderCaustics(ctx, tx0, ty0, tx1, ty1)
    if (this.mode === 'drown') this.renderTide(ctx, tx0, ty0, tx1, ty1)
    if (zone6Arena.active) this.renderBands(ctx)
    this.renderRipples(ctx)
    if (this.mode === 'hub') this.renderHeartGate(ctx)
    for (const b of zone6Lights.braziers) {
      drawSprite(ctx, b.lit ? (b.fuel < BR.gutterSeconds ? 'z6_brazier_gutter' : 'z6_brazier_lit') : 'z6_brazier_unlit', b.x, b.y - 4)
    }
  }

  private renderCaustics(ctx: CanvasRenderingContext2D, tx0: number, ty0: number, tx1: number, ty1: number): void {
    const t = this.anim
    ctx.save()
    ctx.globalAlpha = 0.26
    ctx.strokeStyle = C.caustic
    ctx.lineWidth = 1.5
    ctx.beginPath()
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        if (tiles[ty * gw + tx] !== T_WATER) continue
        if (((tx * 5 + ty * 3) & 1) !== 0) continue
        const x = tx * TILE + 16
        const y = ty * TILE + 16
        const p = Math.sin(t * 1.3 + tx * 0.55 + ty * 0.9)
        const q = Math.cos(t * 0.9 + tx * 0.31 - ty * 0.44)
        ctx.moveTo(x - 10 + p * 5, y + q * 6)
        ctx.lineTo(x + 10 + p * 5, y + q * 6 - 3)
      }
    }
    ctx.stroke()
    ctx.restore()
  }

  /** The surge, drawn on the floor: the water visibly climbs each tile. */
  private renderTide(ctx: CanvasRenderingContext2D, tx0: number, ty0: number, tx1: number, ty1: number): void {
    if (this.surge === 'calm') return
    const t = this.anim
    const rising = this.surge === 'telegraph'
    const k = rising ? 1 - Math.max(0, this.surgeT) / SURGE.telegraph : 1

    ctx.save()
    if (rising) {
      const h = Math.round(6 + k * (TILE - 6))
      ctx.globalAlpha = 0.16 + 0.3 * k
      ctx.fillStyle = C.surge
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          ctx.fillRect(tx * TILE, ty * TILE + (TILE - h), TILE, h)
        }
      }
      ctx.globalAlpha = 0.35 + 0.45 * k
      ctx.fillStyle = C.floodHot
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          const wob = Math.sin(t * 9 + tx * 0.8 + ty * 0.5) * 1.5
          ctx.fillRect(tx * TILE, ty * TILE + (TILE - h) + wob, TILE, 2)
        }
      }
    } else {
      ctx.globalAlpha = 0.34 + 0.12 * Math.sin(t * 14)
      ctx.fillStyle = C.flood
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          ctx.fillRect(tx * TILE, ty * TILE, TILE, TILE)
        }
      }
      // the hot rim: unblockable damage carries a red edge, and every shoreline
      // of live flood burns so "this whole shape is damage" is one glance
      ctx.globalAlpha = 0.55 + 0.35 * Math.sin(t * 16)
      ctx.fillStyle = C.floodEdge
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (tiles[ty * gw + tx] !== T_WATER) continue
          const x = tx * TILE
          const y = ty * TILE
          if (tileKind(tx, ty - 1) !== T_WATER) ctx.fillRect(x, y, TILE, 3)
          if (tileKind(tx, ty + 1) !== T_WATER) ctx.fillRect(x, y + TILE - 3, TILE, 3)
          if (tileKind(tx - 1, ty) !== T_WATER) ctx.fillRect(x, y, 3, TILE)
          if (tileKind(tx + 1, ty) !== T_WATER) ctx.fillRect(x + TILE - 3, y, 3, TILE)
        }
      }
    }
    ctx.restore()
  }

  /** The Sovereign's flooded edges, drawn beneath the fight. */
  private renderBands(ctx: CanvasRenderingContext2D): void {
    const t = this.anim
    ctx.save()
    for (const q of zone6Arena.bands) {
      if (q.state === 0) continue
      if (q.state > 0) {
        const k = Math.min(1, q.state)
        const h = Math.round(4 + k * (q.h - 4))
        ctx.globalAlpha = 0.18 + 0.32 * k
        ctx.fillStyle = C.surge
        ctx.fillRect(q.x, q.y + (q.h - h), q.w, h)
        ctx.globalAlpha = 0.5 + 0.4 * k
        ctx.fillStyle = C.floodHot
        ctx.fillRect(q.x, q.y + (q.h - h) + Math.sin(t * 10) * 2, q.w, 3)
      } else {
        ctx.globalAlpha = 0.4 + 0.12 * Math.sin(t * 13)
        ctx.fillStyle = C.flood
        ctx.fillRect(q.x, q.y, q.w, q.h)
        ctx.globalAlpha = 0.6 + 0.35 * Math.sin(t * 16)
        ctx.fillStyle = C.floodEdge
        ctx.fillRect(q.x, q.y, q.w, 4)
        ctx.fillRect(q.x, q.y + q.h - 4, q.w, 4)
        ctx.fillRect(q.x, q.y, 4, q.h)
        ctx.fillRect(q.x + q.w - 4, q.y, 4, q.h)
      }
    }
    ctx.restore()
  }

  private renderRipples(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    ctx.lineWidth = 2
    for (const r of ripples) {
      if (r.t <= 0) continue
      const k = 1 - r.t / r.life
      ctx.globalAlpha = 0.55 * (1 - k)
      ctx.strokeStyle = r.color
      ctx.beginPath()
      ctx.arc(r.x, r.y, r.r0 + (r.r1 - r.r0) * k, 0, TAU)
      ctx.stroke()
    }
    ctx.restore()
  }

  /** The heart-gate, above the stair: it breathes, and it counts your seals. */
  private renderHeartGate(ctx: CanvasRenderingContext2D): void {
    const run = this.ctx
    if (!run) return
    const gate = run.room as unknown as { bossDoor: { tx: number; ty: number } | null }
    const t = gate.bossDoor
    if (!t) return
    const x = t.tx * TILE + TILE / 2
    const y = t.ty * TILE + TILE / 2
    const beat = 0.5 + 0.5 * Math.sin(this.anim * 2.2)
    ctx.save()
    ctx.globalAlpha = 0.3 + 0.25 * beat
    ctx.fillStyle = C.vein
    ctx.beginPath()
    ctx.ellipse(x, y + 22, 54 + beat * 10, 26 + beat * 5, 0, 0, TAU)
    ctx.fill()
    ctx.restore()
    // The sprite only: game/rooms.ts already labels the stair, and a second line
    // of text under it is clutter, not information.
    drawSprite(ctx, 'z6_heartgate', x, y + 6, { scale: 1.6 })
  }

  /**
   * Over the bodies: the dark, the tells the dark may never eat, and the pulse
   * of the meter itself. The zone is the clock, so the room shows the clock.
   */
  renderOver(d: Draw): void {
    const run = this.ctx
    if (!run || gw === 0) return
    viewRect(d)
    const dark = zone6Lights.dark
    if (dark) this.renderMask(d)
    this.renderMotes(d)
    if (dark) this.renderBrazierTops(d, run)
    this.renderTells(d, run)
    if (dark) this.renderProjectiles(d, run)
    if (dark) this.renderUnlitBody(d, run)
    if (zone6Arena.active) this.renderLanes(d)
    if (this.mode === 'drown' && this.surge !== 'calm') this.renderSurgeCue(d, run)
    this.renderPulse(d)
  }

  /** The mask: one offscreen canvas, filled dark, holes punched out of it. */
  private renderMask(d: Draw): void {
    const w = Math.ceil(viewR.w)
    const h = Math.ceil(viewR.h)
    if (!this.mask || this.mask.width < w || this.mask.height < h) {
      this.mask = document.createElement('canvas')
      this.mask.width = Math.max(w, 1024)
      this.mask.height = Math.max(h, 640)
      this.maskCtx = this.mask.getContext('2d')
    }
    const m = this.maskCtx
    if (!m || !this.mask) return
    const ox = viewR.x
    const oy = viewR.y

    m.setTransform(1, 0, 0, 1, 0, 0)
    m.globalCompositeOperation = 'source-over'
    m.clearRect(0, 0, this.mask.width, this.mask.height)
    m.fillStyle = C.dark
    m.globalAlpha = 0.965
    m.fillRect(0, 0, w, h)
    m.globalAlpha = 1
    m.globalCompositeOperation = 'destination-out'

    const hole = (x: number, y: number, r: number, core: number, strength: number): void => {
      if (r <= 0) return
      const cx = x - ox
      const cy = y - oy
      if (cx < -r || cy < -r || cx > w + r || cy > h + r) return
      const g = m.createRadialGradient(cx, cy, Math.max(1, r * core * 0.6), cx, cy, r)
      g.addColorStop(0, `rgba(0,0,0,${strength})`)
      g.addColorStop(0.62, `rgba(0,0,0,${strength * 0.72})`)
      g.addColorStop(1, 'rgba(0,0,0,0)')
      m.fillStyle = g
      m.beginPath()
      m.arc(cx, cy, r, 0, TAU)
      m.fill()
    }

    hole(zone6Lights.lampX, zone6Lights.lampY, zone6Lights.lampRadius * (1 + 0.015 * Math.sin(this.anim * 2.1)), DARK.lampCore, 1)

    for (const b of zone6Lights.braziers) {
      if (!b.lit) continue
      const gut = b.fuel < BR.gutterSeconds
      const flick = gut
        ? 0.72 + 0.28 * Math.sin(this.anim * 21 + b.tx) * Math.sin(this.anim * 7.3 + b.ty)
        : 1 + 0.03 * Math.sin(this.anim * 9 + b.tx * 1.7)
      hole(b.x, b.y - 6, brazierRadius(b) * flick, BR.core, 1)
      if (b.flare > 0) hole(b.x, b.y - 6, BR.radius * (1.9 - b.flare), 0.2, 0.85)
    }

    // FAIRNESS, and it is not negotiable: the Sovereign is never invisible. It
    // carries its own light, and so does the husk-heart while its clock runs.
    if (zone6Arena.sovereign.on) {
      hole(zone6Arena.sovereign.x, zone6Arena.sovereign.y, zone6Arena.sovereign.r, 0.45, 1)
    }
    if (zone6Arena.heart.on) hole(zone6Arena.heart.x, zone6Arena.heart.y, 110, 0.4, 1)

    // a shot in the dark is a lamp
    const pool = (this.ctx as unknown as RunInternals).projectilePool
    if (pool) {
      const list = pool.live
      for (let i = 0; i < list.length; i++) {
        const p = list[i]
        if (!p.alive || p.friendly) continue
        hole(p.pos.x, p.pos.y, 40, 0.15, 0.72)
      }
    }

    m.globalCompositeOperation = 'source-over'
    d.ctx.drawImage(this.mask, 0, 0, w, h, ox, oy, w, h)
  }

  /** Drifting motes — the Gloom's own dust, and the only thing that is always visible. */
  private renderMotes(d: Draw): void {
    const ctx = d.ctx
    const t = this.anim
    ctx.save()
    for (let i = 0; i < 46; i++) {
      const seed = i * 97.13
      const px = viewR.x + ((seed * 37 + t * (12 + (i % 5) * 6)) % viewR.w)
      const py = viewR.y + ((seed * 61 + Math.sin(t * 0.4 + i) * 40 + t * 9) % viewR.h)
      const k = 0.25 + 0.45 * (0.5 + 0.5 * Math.sin(t * 1.6 + i))
      ctx.globalAlpha = k * 0.5
      ctx.fillStyle = i % 4 === 0 ? C.mote : C.vein
      ctx.fillRect(Math.round(px), Math.round(py), 2, 2)
    }
    ctx.restore()
  }

  private renderBrazierTops(d: Draw, run: RunCtx): void {
    const ctx = d.ctx
    const t = this.anim
    ctx.save()
    for (const b of zone6Lights.braziers) {
      const x = b.x
      const y = b.y - 10
      if (b.lit) {
        const gut = b.fuel < BR.gutterSeconds
        const flick = gut ? 0.45 + 0.55 * Math.abs(Math.sin(t * 17 + b.tx)) : 0.82 + 0.18 * Math.sin(t * 7 + b.ty)
        const r = 13 * flick
        ctx.globalAlpha = 0.85
        ctx.fillStyle = C.ember
        ctx.beginPath()
        ctx.ellipse(x, y, r * 0.62, r, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = C.lampHot
        ctx.beginPath()
        ctx.ellipse(x, y + 2, r * 0.32, r * 0.55, 0, 0, TAU)
        ctx.fill()
        const frac = Math.max(0, Math.min(1, b.fuel / BR.burnSeconds))
        ctx.globalAlpha = gut ? 0.55 + 0.35 * Math.sin(t * 16) : 0.4
        ctx.strokeStyle = gut ? '#ff7a42' : C.lamp
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(b.x, b.y - 4, 17, -Math.PI / 2, -Math.PI / 2 + frac * TAU)
        ctx.stroke()
      } else {
        ctx.globalAlpha = 0.4 + 0.16 * Math.sin(t * 1.7 + b.tx)
        ctx.fillStyle = C.emberDead
        ctx.beginPath()
        ctx.ellipse(x, y + 4, 6, 3.5, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 0.26 + 0.1 * Math.sin(t * 2.3 + b.ty)
        ctx.strokeStyle = C.lamp
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(b.x, b.y - 4, 13, 0, TAU)
        ctx.stroke()
      }
      if (!b.lit && Math.hypot(b.x - run.player.pos.x, b.y - run.player.pos.y) < BR.reach + 26) {
        ctx.globalAlpha = 1
        textAt.x = b.x
        textAt.y = b.y - 34
        d.text('E — LIGHT', textAt, 11, C.lamp)
      }
    }
    ctx.restore()
  }

  /**
   * The counterplay set, painted after the dark. Eye-glints for anything the
   * dark is hiding, every windup at full strength, husk chevrons, the Courtier's
   * red glint, the Shellback's shell, the Tidecaller's line.
   */
  private renderTells(d: Draw, run: RunCtx): void {
    const ctx = d.ctx
    const t = this.anim
    const dark = zone6Lights.dark
    const list = run.enemies
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const e = list[i] as Body
      if (!e.alive) continue
      if (e.tags.z6unlit) continue

      // --- the husk: a pool, a closing ring, and a chevron over it
      if (e.def.id === PALE.huskId) {
        const max = Math.max(0.001, num(e.tags.huskMax, PALE.huskSeconds))
        const left = Math.max(0, num(e.tags.huskT, 0))
        const k = 1 - Math.max(0, Math.min(1, left / max))
        const tended = e.tags.tended === true
        const urgent = left <= 0.7
        const glow = tended ? C.tend : urgent ? '#ffbe5c' : C.huskGlow
        const pulse = 0.55 + 0.45 * Math.sin(t * (urgent ? 16 : 6) + e.pos.x * 0.05)
        ctx.globalAlpha = 0.3
        ctx.fillStyle = '#160f24'
        ctx.beginPath()
        ctx.ellipse(e.pos.x, e.pos.y + 6, 26, 13, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 0.2 + 0.22 * pulse
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.ellipse(e.pos.x, e.pos.y + 6, 30 + pulse * 6, 15 + pulse * 4, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 0.5
        ctx.strokeStyle = '#0b0714'
        ctx.lineWidth = 4
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y + 4, 19, 0, TAU)
        ctx.stroke()
        ctx.globalAlpha = 1
        ctx.strokeStyle = glow
        ctx.lineWidth = 3.5
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y + 4, 19, -Math.PI / 2, -Math.PI / 2 + TAU * k)
        ctx.stroke()
        const y = e.pos.y - 28 + Math.sin(t * 4 + e.pos.y * 0.05) * 2
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.moveTo(e.pos.x, y - 6)
        ctx.lineTo(e.pos.x + 6, y + 3)
        ctx.lineTo(e.pos.x - 6, y + 3)
        ctx.closePath()
        ctx.fill()
        ctx.globalAlpha = 1
        continue
      }

      // --- eye-glints: what the dark leaves you
      if (dark) {
        const lit = zone6Lights.lightAt(e.pos.x, e.pos.y)
        const hidden = 1 - Math.min(1, lit / 0.5)
        if (hidden > 0.02) {
          const eye = e.def.id === 'z6-moth' ? '#d9bcff' : e.def.id === 'z6-caster' ? C.homing : '#ffb066'
          const off = e.radius * 0.34
          const yy = e.pos.y - e.radius * 0.55 + Math.sin(e.bob * 7 + i) * 0.8
          ctx.globalAlpha = 0.5 + 0.45 * hidden
          ctx.fillStyle = eye
          ctx.fillRect(Math.round(e.pos.x - off - 1), Math.round(yy), 2, 2)
          ctx.fillRect(Math.round(e.pos.x + off - 1), Math.round(yy), 2, 2)
          ctx.globalAlpha = 0.12 * hidden
          ctx.beginPath()
          ctx.arc(e.pos.x, yy + 1, 9, 0, TAU)
          ctx.fill()
        }
      }

      // --- every windup, whatever the light is doing
      if (dark && e.telegraph > 0) {
        const k = 1 - Math.min(1, e.telegraph / 0.6)
        ctx.globalAlpha = 0.35 + 0.45 * k
        ctx.strokeStyle = '#ffd166'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, e.radius + 8 - k * 4, 0, TAU)
        ctx.stroke()
      }

      // --- the Delver's eruption, drawn where it will land
      const er = num(e.tags.z6eruptR, 0)
      if (er > 0) {
        const k = 1 - Math.min(1, e.telegraph / 0.5)
        ctx.globalAlpha = 0.25 + 0.45 * k
        ctx.strokeStyle = '#ff9a4a'
        ctx.lineWidth = 2 + k * 2
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, er, 0, TAU)
        ctx.stroke()
      }

      // --- the Shellback's shell
      if (e.def.id === 'z6-shellback') {
        const face = num(e.tags.z6face, 0)
        const half = (ai(e as unknown as { def: { ai: Record<string, unknown> } }, 'frontalArcDeg', 130) * DEG) / 2
        const r = 15 + (e.elite === 'bloated' ? 5 : 0)
        if (e.tags.z6block === true) {
          ctx.globalAlpha = 0.85
          ctx.strokeStyle = '#cbb6e8'
          ctx.lineWidth = 5
          ctx.beginPath()
          ctx.arc(e.pos.x, e.pos.y, r, face - half, face + half)
          ctx.stroke()
        } else {
          ctx.globalAlpha = 0.75
          ctx.strokeStyle = '#ff8a6a'
          ctx.lineWidth = 3
          ctx.setLineDash([5, 5])
          ctx.beginPath()
          ctx.arc(e.pos.x, e.pos.y, r, face - half, face + half)
          ctx.stroke()
          ctx.setLineDash([])
        }
      }

      // --- the Tidecaller's line
      const beam = num(e.tags.z6beam, 0)
      if (beam > 0) {
        const pulling = beam === 2
        ctx.globalAlpha = pulling ? 0.9 : 0.4 + 0.3 * Math.sin(t * 16)
        ctx.strokeStyle = C.hook
        ctx.lineWidth = pulling ? 4 : 2
        if (!pulling) ctx.setLineDash([7, 6])
        ctx.beginPath()
        ctx.moveTo(e.pos.x, e.pos.y - 6)
        ctx.lineTo(run.player.pos.x, run.player.pos.y)
        ctx.stroke()
        ctx.setLineDash([])
      }

      // --- the Gravetender's line to its job
      if (e.def.id === 'z6-gravetender' && e.tags.hasWork === true) {
        const gx = num(e.tags.tx, e.pos.x)
        const gy = num(e.tags.ty, e.pos.y)
        const channelling = num(e.tags.chanT, 0) > 0
        ctx.globalAlpha = channelling ? 0.55 : 0.22
        ctx.strokeStyle = C.tend
        ctx.lineWidth = channelling ? 2.5 : 1.5
        ctx.beginPath()
        ctx.moveTo(e.pos.x, e.pos.y)
        ctx.lineTo(gx, gy)
        ctx.stroke()
      }

      // --- the Courtier's red glint
      if (e.tags.glint === true && e.telegraph > 0) {
        const k = 0.4 + 0.6 * Math.abs(Math.sin(t * 26))
        const gx = e.pos.x + e.facing * 11
        const gy = e.pos.y - 10
        ctx.globalAlpha = k
        ctx.fillStyle = '#ff4a4a'
        ctx.beginPath()
        ctx.moveTo(gx, gy - 5)
        ctx.lineTo(gx + 3.5, gy)
        ctx.lineTo(gx, gy + 5)
        ctx.lineTo(gx - 3.5, gy)
        ctx.closePath()
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }
    ctx.restore()
  }

  private renderProjectiles(d: Draw, run: RunCtx): void {
    const pool = (run as unknown as RunInternals).projectilePool
    if (!pool) return
    const ctx = d.ctx
    const list = pool.live
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const p = list[i]
      if (!p.alive || p.friendly) continue
      ctx.globalAlpha = 0.4
      ctx.fillStyle = p.color
      ctx.beginPath()
      ctx.arc(p.pos.x, p.pos.y, p.radius + 4, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
      drawSprite(ctx, p.spriteId, p.pos.x, p.pos.y, { rot: p.def.kind === 'straight' ? p.rot : 0 })
    }
    ctx.restore()
  }

  /** The Unlit: a shape blacker than the dark, and two eyes that do not blink. */
  private renderUnlitBody(d: Draw, run: RunCtx): void {
    const u = this.unlit
    if (!u || !u.alive) return
    const ctx = d.ctx
    const t = this.anim
    const mode = num(u.tags.z6mode, UN_WALK)
    const ut = num(u.tags.z6t, 0)
    let k = 1
    if (mode === UN_RISE) k = Math.min(1, ut / U.riseSeconds)
    else if (mode === UN_SINK) k = Math.max(0, 1 - ut / U.sinkSeconds)

    const x = u.pos.x
    const y = u.pos.y
    const h = 54 * k

    ctx.save()
    ctx.globalAlpha = 0.9
    ctx.fillStyle = C.unlitBody
    ctx.beginPath()
    ctx.ellipse(x, y + 12, 17, 6, 0, 0, TAU)
    ctx.fill()
    ctx.globalAlpha = 1
    ctx.beginPath()
    ctx.moveTo(x - 13, y + 12)
    ctx.lineTo(x - 8, y + 12 - h)
    ctx.quadraticCurveTo(x, y + 2 - h - 8, x + 8, y + 12 - h)
    ctx.lineTo(x + 13, y + 12)
    ctx.closePath()
    ctx.fillStyle = C.unlitBody
    ctx.fill()
    ctx.globalAlpha = 0.22
    ctx.strokeStyle = '#3a2a52'
    ctx.lineWidth = 2
    ctx.stroke()

    if (k > 0.5) {
      const ey = y + 12 - h + 10
      const halt = u.tags.z6halt === 1
      ctx.globalAlpha = halt ? 0.55 + 0.3 * Math.sin(t * 12) : 0.92
      ctx.fillStyle = C.unlitEye
      ctx.fillRect(Math.round(x - 5), Math.round(ey), 3, 3)
      ctx.fillRect(Math.round(x + 3), Math.round(ey), 3, 3)
      ctx.globalAlpha = 0.16
      ctx.beginPath()
      ctx.arc(x, ey + 1, 15, 0, TAU)
      ctx.fill()
    }

    const pd = Math.hypot(x - run.player.pos.x, y - run.player.pos.y)
    if (pd < U.droneRange && mode === UN_WALK) {
      const near = 1 - pd / U.droneRange
      ctx.globalAlpha = 0.1 + 0.35 * near
      ctx.strokeStyle = '#7a63b8'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(x, y + 10, 22 + (1 - near) * 26 + Math.sin(t * 3) * 3, 0, TAU)
      ctx.stroke()
    }
    ctx.restore()
  }

  /** The Sovereign's charge lanes — boss 4's language, and the dark may not eat them. */
  private renderLanes(d: Draw): void {
    const ctx = d.ctx
    const t = this.anim
    ctx.save()
    for (const lane of zone6Arena.lanes) {
      if (!lane.on) continue
      const dx = lane.x1 - lane.x0
      const dy = lane.y1 - lane.y0
      const len = Math.hypot(dx, dy) || 1
      const nx = -dy / len
      const ny = dx / len
      const hw = 30
      const charging = lane.k >= 0
      const k = charging ? lane.k : 1
      ctx.globalAlpha = charging ? 0.1 + 0.3 * k : 0.34
      ctx.fillStyle = charging ? '#c9a6ff' : '#ff9ad6'
      ctx.beginPath()
      ctx.moveTo(lane.x0 + nx * hw, lane.y0 + ny * hw)
      ctx.lineTo(lane.x1 + nx * hw, lane.y1 + ny * hw)
      ctx.lineTo(lane.x1 - nx * hw, lane.y1 - ny * hw)
      ctx.lineTo(lane.x0 - nx * hw, lane.y0 - ny * hw)
      ctx.closePath()
      ctx.fill()
      ctx.globalAlpha = 0.55 + 0.4 * k
      ctx.fillStyle = '#f0e0ff'
      for (let i = 0; i < 9; i++) {
        const f = ((t * 1.9 + i * 0.111) % 1) * len
        const s = Math.sin(t * 6 + i) * hw * 0.55
        ctx.fillRect(Math.round(lane.x0 + (dx / len) * f + nx * s), Math.round(lane.y0 + (dy / len) * f + ny * s), 3, 3)
      }
    }
    ctx.restore()
  }

  private renderSurgeCue(d: Draw, run: RunCtx): void {
    const ctx = d.ctx
    const rising = this.surge === 'telegraph'
    const k = rising ? 1 - Math.max(0, this.surgeT) / SURGE.telegraph : 1
    const band = 120 / viewR.zoom
    const x0 = viewR.x + TILE * 3
    const y0 = viewR.y + TILE * 3
    const w = viewR.w - TILE * 6
    const h = viewR.h - TILE * 6
    const color = rising ? C.surge : C.floodEdge
    const alpha = rising
      ? 0.1 + 0.45 * k * (0.55 + 0.45 * Math.sin(this.anim * 12))
      : 0.4 + 0.18 * Math.sin(this.anim * 18)

    ctx.save()
    ctx.globalAlpha = alpha
    const edge = (gx0: number, gy0: number, gx1: number, gy1: number, rx: number, ry: number, rw: number, rh: number): void => {
      const g = ctx.createLinearGradient(gx0, gy0, gx1, gy1)
      g.addColorStop(0, color)
      g.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = g
      ctx.fillRect(rx, ry, rw, rh)
    }
    edge(0, y0, 0, y0 + band, x0, y0, w, band)
    edge(0, y0 + h, 0, y0 + h - band, x0, y0 + h - band, w, band)
    edge(x0, 0, x0 + band, 0, x0, y0, band, h)
    edge(x0 + w, 0, x0 + w - band, 0, x0 + w - band, y0, band, h)
    ctx.restore()

    textAt.x = run.player.pos.x
    textAt.y = run.player.pos.y - 46
    if (rising) d.text(`THE TIDE — ${Math.max(0, this.surgeT).toFixed(1)}`, textAt, 13, C.floodHot)
    else if (run.tileAt(run.player.pos) === 'W') d.text('GET OUT OF THE WATER', textAt, 14, '#ff8a6a')
  }

  /**
   * THE ZONE IS THE METER. A violet pulse over everything, breathing at a rate
   * and a depth tied to the Gloom — 40% at the door, and it never goes back.
   * It is subtle at the bottom and impossible to ignore at Arrival, which is the
   * whole point: you can feel the clock without reading it.
   */
  private renderPulse(d: Draw): void {
    const g = Math.max(0, Math.min(1, this.gloomSeen))
    const ctx = d.ctx
    const beat = 0.5 + 0.5 * Math.sin(this.anim * (1.1 + g * 2.6))
    const strength = ZONE6_GLOOM.pulseMin + (ZONE6_GLOOM.pulseMax - ZONE6_GLOOM.pulseMin) * g
    const a = strength * (0.55 + 0.45 * beat) + zone6Arena.recoil * 0.5
    if (a <= 0.01) return
    const cx = viewR.x + viewR.w / 2
    const cy = viewR.y + viewR.h / 2
    const r = Math.hypot(viewR.w, viewR.h) * 0.5
    ctx.save()
    const grad = ctx.createRadialGradient(cx, cy, r * 0.45, cx, cy, r)
    grad.addColorStop(0, 'rgba(0,0,0,0)')
    grad.addColorStop(1, `rgba(122,42,216,${a.toFixed(3)})`)
    ctx.fillStyle = grad
    ctx.fillRect(viewR.x, viewR.y, viewR.w, viewR.h)
    if (zone6Arena.recoil > 0) {
      ctx.globalAlpha = zone6Arena.recoil * 0.5
      ctx.fillStyle = C.mote
      ctx.fillRect(viewR.x, viewR.y, viewR.w, viewR.h)
    }
    ctx.restore()
  }

  // --------------------------------------------------------------- test seam

  debugState(): {
    mode: Mode
    gloom: number
    surge: { phase: SurgePhase; t: number; count: number }
    husks: number
    sealed: number
    reformed: number
    braziers: { x: number; y: number; lit: boolean; fuel: number }[]
    litCount: number
    dark: boolean
    lampRadius: number
    unlit: { alive: boolean; x: number; y: number; mode: number; halt: boolean }
    waterTiles: number
    arena: { active: boolean; husks: boolean; dark: boolean; bands: number; flooded: number; sovereign: boolean; heart: boolean }
  } {
    const u = this.unlit
    let flooded = 0
    for (const b of zone6Arena.bands) if (b.state === -1) flooded++
    return {
      mode: this.mode,
      gloom: this.gloomSeen,
      surge: { phase: this.surge, t: this.surgeT, count: this.surges },
      husks: this.huskCount,
      sealed: this.sealedThisRoom,
      reformed: this.reformedThisRoom,
      braziers: zone6Lights.braziers.map((b) => ({ x: b.x, y: b.y, lit: b.lit, fuel: b.fuel })),
      litCount: zone6Lights.litCount(),
      dark: zone6Lights.dark,
      lampRadius: zone6Lights.lampRadius,
      unlit: {
        alive: u != null && u.alive,
        x: u ? u.pos.x : 0,
        y: u ? u.pos.y : 0,
        mode: u ? num(u.tags.z6mode, -1) : -1,
        halt: u ? u.tags.z6halt === 1 : false,
      },
      waterTiles: waterTiles.length,
      arena: {
        active: zone6Arena.active,
        husks: zone6Arena.husks,
        dark: zone6Arena.dark,
        bands: zone6Arena.bands.length,
        flooded,
        sovereign: zone6Arena.sovereign.on,
        heart: zone6Arena.heart.on,
      },
    }
  }

  debugForceSurge(): void {
    if (this.surge === 'calm') this.surgeT = 0
  }

  debugCalm(): void {
    this.surge = 'calm'
    this.surgeT = 1e9
  }

  debugSpawnUnlit(): void {
    if (!this.ctx) return
    this.banishT = 0
    this.unlitTimer = 0
    this.spawnUnlit(this.ctx, false)
  }

  debugSetFuel(i: number, seconds: number): void {
    const b = zone6Lights.braziers[i]
    if (b) b.fuel = seconds
  }
}

const mech = new Gloomheart()
export const zone6Mechanic: ZoneMechanic = mech

/**
 * TEST SEAM (test/zone6.mjs). Reading the zone's three clocks and steering them
 * from a spec is the only way to assert an 18-second surge, a 2-second husk and
 * a stalker that arrives at once without waiting minutes a dozen times over.
 * Inert unless something calls it; nothing in the game does.
 */
export const zone6Debug = {
  state: (): ReturnType<Gloomheart['debugState']> => mech.debugState(),
  forceSurge: (): void => mech.debugForceSurge(),
  calm: (): void => mech.debugCalm(),
  spawnUnlit: (): void => mech.debugSpawnUnlit(),
  setFuel: (i: number, s: number): void => mech.debugSetFuel(i, s),
  light: (i: number): void => z6LightBrazier(i, BR.burnSeconds),
  douse: (i: number): void => z6DouseBrazier(i),
  lights: zone6Lights,
  arena: zone6Arena,
  isWaterAt: z6IsWaterAt,
}

// ===========================================================================
// 6. THE FLOOR LAYER
// One canvas per room: flesh-stone with a grain that is not quite masonry,
// gloom-veins running out of every wall face, and the digested memory of
// whichever zone this spur used to be. Composited once at room build.
// ===========================================================================

function paintFloorLayer(ctx: RunCtx, mode: Mode): HTMLCanvasElement | null {
  if (gw === 0 || gh === 0) return null
  const c = document.createElement('canvas')
  c.width = gw * TILE
  c.height = gh * TILE
  const g = c.getContext('2d')
  if (!g) return null
  g.imageSmoothingEnabled = false
  const hash = (x: number, y: number): number => (((x * 73856093) ^ (y * 19349663)) >>> 0) % 1000

  // --- the water the Gloom is digesting
  for (let ty = 0; ty < gh; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (tiles[ty * gw + tx] !== T_WATER) continue
      const x = tx * TILE
      const y = ty * TILE
      let open = 0
      if (tileKind(tx - 1, ty) === T_WATER) open++
      if (tileKind(tx + 1, ty) === T_WATER) open++
      if (tileKind(tx, ty - 1) === T_WATER) open++
      if (tileKind(tx, ty + 1) === T_WATER) open++
      g.fillStyle = open >= 4 ? C.waterDeep : C.water
      g.fillRect(x, y, TILE, TILE)
      g.globalAlpha = 0.16
      g.fillStyle = C.waterEdge
      for (let i = 0; i < 2; i++) {
        const yy = y + 8 + i * 13 + ((tx * 7 + ty * 5) % 5)
        g.fillRect(x + ((tx * 3 + ty) % 6), yy, TILE - 8, 1)
      }
      g.globalAlpha = 1
    }
  }
  g.globalAlpha = 0.5
  g.fillStyle = C.waterEdge
  for (let ty = 0; ty < gh; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (tiles[ty * gw + tx] !== T_WATER) continue
      const x = tx * TILE
      const y = ty * TILE
      if (tileKind(tx, ty - 1) !== T_WATER) g.fillRect(x, y, TILE, 2)
      if (tileKind(tx, ty + 1) !== T_WATER) g.fillRect(x, y + TILE - 2, TILE, 2)
      if (tileKind(tx - 1, ty) !== T_WATER) g.fillRect(x, y, 2, TILE)
      if (tileKind(tx + 1, ty) !== T_WATER) g.fillRect(x + TILE - 2, y, 2, TILE)
    }
  }
  g.globalAlpha = 1

  // --- the causeway that used to be zone 2's
  for (let ty = 0; ty < gh; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (tiles[ty * gw + tx] !== T_STONE) continue
      const x = tx * TILE
      const y = ty * TILE
      g.fillStyle = C.stone
      g.fillRect(x, y, TILE, TILE)
      g.fillStyle = shade(C.stone, -0.18)
      for (let i = 2; i < TILE; i += 9) g.fillRect(x + ((tx + ty) % 4), y + i, TILE - 4, 1)
      if (tileKind(tx, ty - 1) !== T_STONE) {
        g.fillStyle = C.stoneTop
        g.fillRect(x, y, TILE, 3)
      }
      if (tileKind(tx, ty + 1) !== T_STONE) {
        g.fillStyle = C.stoneShadow
        g.fillRect(x, y + TILE - 3, TILE, 3)
      }
    }
  }

  // --- WHAT THIS SPUR USED TO BE. The rooms are one place — inside the Gloom —
  // so the floor carries the memory instead of the palette: the Pale Spur keeps
  // the court's marble grout under the flesh, the Dark Spur keeps the mines'
  // rails and sleepers, and the Drowning Spur has its causeway above.
  if (mode === 'pale') {
    g.globalAlpha = 0.14
    g.strokeStyle = '#cfc6dd'
    g.lineWidth = 1
    for (let ty = 1; ty < gh - 1; ty++) {
      for (let tx = 1; tx < gw - 1; tx++) {
        if (ctx.isWall(tx, ty)) continue
        const x = tx * TILE
        const y = ty * TILE
        // big court flags: a 2x2 grout grid, so the floor reads as laid stone
        if (tx % 2 === 0 && ty % 2 === 0) g.strokeRect(x + 0.5, y + 0.5, TILE * 2 - 1, TILE * 2 - 1)
      }
    }
    g.globalAlpha = 0.1
    g.fillStyle = '#e8e2f0'
    for (let ty = 1; ty < gh - 1; ty++) {
      for (let tx = 1; tx < gw - 1; tx++) {
        if (!ctx.isWall(tx, ty) || ctx.isWall(tx, ty + 1)) continue
        // the ghost of a funeral banner hanging under each arch
        const q = hash(tx * 5, ty * 3)
        if (q % 100 >= 40) continue
        g.fillRect(tx * TILE + 10, (ty + 1) * TILE, 12, 18 + (q % 14))
      }
    }
    g.globalAlpha = 1
  } else if (mode === 'dark' || mode === 'arena') {
    // rails down any long open run, with sleepers — the mines, swallowed
    g.globalAlpha = 0.5
    for (let ty = 2; ty < gh - 2; ty++) {
      let run = 0
      for (let tx = 1; tx <= gw - 1; tx++) {
        const open = tx < gw - 1 && !ctx.isWall(tx, ty)
        if (open) {
          run++
          continue
        }
        if (run >= 9 && hash(tx, ty) % 100 < 42) {
          const x0 = (tx - run) * TILE
          const x1 = tx * TILE
          const y = ty * TILE + TILE / 2
          g.strokeStyle = '#241c33'
          g.lineWidth = 3
          g.beginPath()
          g.moveTo(x0, y - 5)
          g.lineTo(x1, y - 5)
          g.moveTo(x0, y + 5)
          g.lineTo(x1, y + 5)
          g.stroke()
          g.strokeStyle = '#4a3d6b'
          g.lineWidth = 1
          g.beginPath()
          g.moveTo(x0, y - 6)
          g.lineTo(x1, y - 6)
          g.moveTo(x0, y + 4)
          g.lineTo(x1, y + 4)
          g.stroke()
          g.fillStyle = '#1c1428'
          for (let x = x0 + 6; x < x1 - 4; x += 13) g.fillRect(x, y - 8, 3, 17)
        }
        run = 0
      }
    }
    // timber struts against every south-facing face
    for (let ty = 0; ty < gh - 1; ty++) {
      for (let tx = 1; tx < gw - 1; tx++) {
        if (!ctx.isWall(tx, ty) || ctx.isWall(tx, ty + 1)) continue
        const q = hash(tx * 7, ty * 11)
        if (q % 100 >= 26) continue
        const x = tx * TILE + 4 + (q % 12)
        const y = (ty + 1) * TILE
        const len = 12 + (q % 16)
        g.fillStyle = '#241a2e'
        g.fillRect(x - 2, y - 2, 4, len)
        g.fillStyle = '#3d2f4a'
        g.fillRect(x - 2, y - 2, 1, len)
        g.fillRect(x - 5, y - 3, 10, 3)
      }
    }
    g.globalAlpha = 1
  }

  // --- gloom-veins, bleeding down out of every wall face, and the motes on them
  for (let ty = 0; ty < gh - 1; ty++) {
    for (let tx = 0; tx < gw; tx++) {
      if (!ctx.isWall(tx, ty) || ctx.isWall(tx, ty + 1)) continue
      const q = hash(tx * 3 + 1, ty * 7 + 5)
      if (q % 100 >= 46) continue
      const x = tx * TILE + 4 + (q % 22)
      const y = (ty + 1) * TILE - 2
      const len = 12 + (q % 24)
      g.strokeStyle = C.veinDim
      g.lineWidth = 3
      g.beginPath()
      g.moveTo(x, y)
      g.quadraticCurveTo(x + ((q % 9) - 4), y + len * 0.55, x + ((q % 13) - 6), y + len)
      g.stroke()
      g.strokeStyle = C.vein
      g.lineWidth = 1
      g.stroke()
      const glow = g.createRadialGradient(x, y + len, 1, x, y + len, 24)
      glow.addColorStop(0, 'rgba(160,107,255,0.26)')
      glow.addColorStop(1, 'rgba(160,107,255,0)')
      g.fillStyle = glow
      g.fillRect(x - 24, y + len - 24, 48, 48)
    }
  }

  // --- the flesh grain: not masonry, and never quite still
  g.globalAlpha = 0.16
  for (let ty = 1; ty < gh - 1; ty++) {
    for (let tx = 1; tx < gw - 1; tx++) {
      if (tiles[ty * gw + tx] !== T_OTHER || ctx.isWall(tx, ty)) continue
      const q = hash(tx * 11 + 3, ty * 5 + 9)
      if (q % 100 >= 30) continue
      g.fillStyle = q % 2 === 0 ? C.fleshHi : C.fleshLo
      const x = tx * TILE + (q % 18)
      const y = ty * TILE + ((q >> 3) % 18)
      g.fillRect(x, y, 8 + (q % 7), 3)
    }
  }
  g.globalAlpha = 1

  return c
}

// ===========================================================================
// 7. SPRITES
// The zone's own furniture. Bodies are re-dressed with the painters their home
// zones registered — that is the point of a memory — so what is painted here is
// only what the Gloomheart itself is made of.
// ===========================================================================

function pstr(p: Record<string, number | string>, k: string, dflt: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : dflt
}
function pnum(p: Record<string, number | string>, k: string, dflt: number): number {
  const v = p[k]
  return typeof v === 'number' ? v : dflt
}

/** The heart-gate: a wet arch of flesh-stone with a shut sphincter of veins. */
registerPainter('z6_heartgate', (a: PixelArt, _p, _pal) => {
  const s = a.size
  const cx = s / 2
  const flesh = C.flesh
  const lo = shade(flesh, -0.45)
  const hi = shade(flesh, 0.3)

  a.ellipse(cx, s * 0.86, s * 0.46, s * 0.1, lo)
  // the arch itself, thick and organic
  for (let y = 0; y < Math.round(s * 0.7); y++) {
    const t = y / (s * 0.7)
    const w = Math.round(s * (0.46 - 0.1 * Math.sin(t * Math.PI)))
    a.hline(Math.round(cx - w), Math.round(s * 0.18 + y), w * 2, y % 7 === 6 ? lo : flesh)
  }
  a.ellipse(cx, s * 0.2, s * 0.46, s * 0.16, hi)
  // the gate: a shut vertical seam, wet and lit from behind
  a.ellipse(cx, s * 0.54, s * 0.24, s * 0.3, '#0a0614')
  a.ellipse(cx, s * 0.54, s * 0.15, s * 0.22, shade(C.vein, -0.55))
  a.vline(Math.round(cx), Math.round(s * 0.3), Math.round(s * 0.48), C.vein)
  a.set(Math.round(cx), Math.round(s * 0.54), '#f0e0ff')
  // veins fanning off the lintel
  for (let i = -3; i <= 3; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.12)
    a.line(x, Math.round(s * 0.2), Math.round(x + i * 3), Math.round(s * 0.06), C.veinDim)
    a.set(Math.round(x + i * 3), Math.round(s * 0.05), C.vein)
  }
})

registerRecipe('z6_heartgate', { key: 'z6_heartgate', size: 46 })

/** A brazier, three states — the same iron the mines used, corroded violet. */
registerPainter('z6_brazier', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const hot = pnum(p, 'hot', 0)
  const iron = '#2b2338'
  const ironHi = '#5a4a78'
  a.line(Math.round(cx - s * 0.2), Math.round(s * 0.62), Math.round(cx - s * 0.3), Math.round(s * 0.92), iron)
  a.line(Math.round(cx + s * 0.2), Math.round(s * 0.62), Math.round(cx + s * 0.3), Math.round(s * 0.92), iron)
  a.line(Math.round(cx), Math.round(s * 0.66), Math.round(cx), Math.round(s * 0.94), iron)
  a.ellipse(cx, s * 0.94, s * 0.3, s * 0.05, '#080513')
  a.ellipse(cx, s * 0.58, s * 0.32, s * 0.16, iron)
  a.ellipse(cx, s * 0.52, s * 0.3, s * 0.12, shade(iron, -0.4))
  a.ellipse(cx, s * 0.5, s * 0.26, s * 0.09, hot > 0 ? shade(C.ember, -0.2 + hot * 0.3) : '#160f24')
  a.ring(cx, s * 0.56, s * 0.32, ironHi)
  if (hot > 0) {
    a.ellipse(cx, s * 0.48, s * 0.18, s * 0.07, hot > 0.5 ? '#ffd08a' : '#7a4a2a')
    a.ellipse(cx, s * 0.4, s * 0.1, s * 0.12, hot > 0.5 ? C.ember : '#4a3a34')
    if (hot > 0.5) {
      a.ellipse(cx, s * 0.34, s * 0.05, s * 0.08, C.lampHot)
      a.set(Math.round(cx), Math.round(s * 0.26), '#fffbe8')
    }
  } else {
    a.set(Math.round(cx - 2), Math.round(s * 0.5), '#4a3a34')
    a.set(Math.round(cx + 3), Math.round(s * 0.51), '#4a3a34')
  }
})

registerRecipe('z6_brazier_unlit', { key: 'z6_brazier', size: 30, params: { hot: 0 } })
registerRecipe('z6_brazier_lit', { key: 'z6_brazier', size: 30, params: { hot: 1 } })
registerRecipe('z6_brazier_gutter', { key: 'z6_brazier', size: 30, params: { hot: 0.4 } })

/** The Delver's plume, digested: the flesh-stone itself moving. */
registerPainter('z6_plume', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const dirt = pstr(p, 'dirt', C.flesh)
  const hi = shade(dirt, 0.35)
  const lo = shade(dirt, -0.45)
  a.ellipse(cx, s * 0.72, s * 0.36, s * 0.16, lo)
  a.ellipse(cx, s * 0.68, s * 0.3, s * 0.13, dirt)
  a.ellipse(cx, s * 0.63, s * 0.18, s * 0.08, hi)
  a.ellipse(cx, s * 0.66, s * 0.09, s * 0.05, '#0a0614')
  for (let i = -3; i <= 3; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.11)
    a.set(x, Math.round(s * 0.5 + Math.abs(i) * 2), C.vein)
    a.set(x + (i > 0 ? 1 : -1), Math.round(s * 0.44 + Math.abs(i) * 3), hi)
  }
})

registerRecipe('z6_plume', { key: 'z6_plume', size: 30 })

// ===========================================================================
// registry
// ===========================================================================

// A projectile's sprite has to be registered by hand: game/zones.ts only wires
// the def. Both borrow the painters their originals used — a bolt you have
// already learned to read must still look like one.
registerRecipe('z6-griefshard', { key: 'z3_grief', size: 17, params: { color: ZONE6_PROJECTILES[0].color } })
registerRecipe('z6-deepbolt', { key: 'deepbolt', size: 15, params: { color: ZONE6_PROJECTILES[1].color } })

registerZoneContent({
  enemies: [...ZONE6_ENEMIES, ...ZONE6_REFORMED],
  projectiles: ZONE6_PROJECTILES,
  generators: ZONE6_GENERATORS,
})
registerZone(ZONE6, zone6Mechanic)
